import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { AngularFirestore } from "@angular/fire/compat/firestore";
import { DomSanitizer } from "@angular/platform-browser";
import DOMPurify from "dompurify";
import {
  CommunityChannel,
  CommunityService,
  ContentService,
  FSP_COMMUNITY_STRING_ID,
  HelperService,
  MessageEmbed,
  ProductService,
  ReactionType,
  StorageKeys,
  StorageService,
  UserService,
  WindowService,
  buildProfileSearchFields,
  environment,
  getMyReactions,
  normalizeReactions,
  profileSearchFieldsNeedUpdate,
  resolveLocalizedString,
  toggleReaction,
  UnreadCandidates,
  UnreadSummary,
  UNREAD_MY_POSTS_VIEW,
  UNREAD_SAVED_POSTS_VIEW,
  collectUnreadCandidates,
  summarizeUnread,
  unreadCandidateThreadIds,
  featuredUpdate,
  isFeaturedNow,
  rememberFeaturedChange,
  sortFeaturedPosts,
} from "@mypxplat/xplat/core";
import { SearchClient, searchClient } from "@algolia/client-search";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { BehaviorSubject, firstValueFrom, forkJoin, interval, Observable, Subject, of } from "rxjs";
import { map, skip, takeUntil, throttle } from "rxjs/operators";
import { BetaService } from "./beta.service";
import { FirebaseService } from "./firebase.service";

/** Channels a per-community notification may arrive on — `notificationPreferences.communities[stringId]`. */
export interface CommunityChannelPrefs {
  email: boolean;
  mobile: boolean;
  desktop: boolean;
}

/**
 * Which events generate a per-community notification at all — `communityNotificationPreferences[stringId]`.
 * The keys are the Firestore field names paeapi's `notificationTopicPref.ts` reads; renaming one here
 * without migrating stored profiles silently resets that pref to its default for every user.
 */
export interface CommunityTopicPrefs {
  new_posts: boolean;
  replies_to_me: boolean;
  replies_in_my_threads: boolean;
  mentions: boolean;
  reactions_on_my_posts: boolean;
}

/**
 * The member's settings for ALL communities — `user_profiles/{uid}.communityDefaults`.
 *
 * Every per-community setting resolves key by key: this community's override if it has one,
 * then these defaults, then the built-in default. paeapi resolves notifications the same way
 * (`notificationTopicPref.ts`, `resolveNotificationChannels.ts`), so what the modal shows is
 * what the server enforces. Feed layout is global only: with every community in one feed, a
 * per-community value stopped meaning anything. The signature has its own all-communities
 * field, `defaultCommunitySignature` (see `getCommunitySignature`).
 */
export interface CommunityDefaults {
  channels?: CommunityChannelPrefs;
  topics?: Partial<CommunityTopicPrefs>;
  sortMode?: "recentactivity" | "date";
  viewMode?: "list" | "detailed";
  /** Communities hub, All communities: show favorite channels or every channel. */
  channelRail?: "favorites" | "all";
  /**
   * What the hub opens on: Favorite Channels (when there are any) or All Channels. Only the
   * starting view — the member can switch either way once it's open.
   */
  startView?: "favorites" | "all";
}

/** What a community would get with no override: the member's defaults over the built-ins. */
export interface ResolvedCommunityDefaults {
  channels: CommunityChannelPrefs;
  topics: CommunityTopicPrefs;
  sortMode: "recentactivity" | "date";
  viewMode: "list" | "detailed";
  startView: "favorites" | "all";
}

/** Built-in notification defaults. Must match paeapi's `DEFAULT_ALLOWED`. */
const BUILT_IN_TOPICS: CommunityTopicPrefs = { new_posts: false, replies_to_me: true, replies_in_my_threads: true, mentions: true, reactions_on_my_posts: true };
const BUILT_IN_CHANNELS: CommunityChannelPrefs = { email: true, mobile: true, desktop: true };

/** localStorage key prefix for the last author list seen per beta (`<prefix><betaStringId>`). */
const BETA_AUTHORS_CACHE_PREFIX = "myp.betaAuthors.";
/** Last-seen channel list per community, so the rail and feed don't wait on paeapi. */
const CHANNELS_CACHE_PREFIX = "myp.communityChannels.";

/**
 * `communityListBackCache.key` when the Communities hub opens a post as a full page (narrow
 * screens): the community page that hosts it sends its back button to the cache's `returnUrl`,
 * the hub as it was, rather than to its own feed.
 */
export const HUB_POST_BACK_KEY = "communities-hub";

@Injectable()
export class WebCommunityService extends CommunityService {
  private scripts: any = {};

  public attachingExistingFile: any;
  public attachingPublicFileLink: any;

  public activeThreadMessages: Array<any>;
  public activeThreadMessagesSubscription: any;
  public threadMessageEvent$: Subject<string> = new Subject();

  // cache profile results so we can display them when navigating back.
  public activeProfileResults: Array<any>;
  public activeThread: any;
  public missedMessageSubscription: any;
  public unreadCounts: any = {
    threads: {},
    total: 0,
  };

  public viewing: any = {};

  public userVideoData: Array<any>;
  public userVideoDataMap: any = {};

  public channelSplashes: any = {
    learning_studio_one:
      '<div class="trans-bg p-10 m-t-10 m-b-10 border-radius10" style="max-width: 600px; margin-left: auto; margin-right: auto;">Welcome to the Learning Studio One channel. Use this space to ask questions or share your knowledge about the world\'s most versatile DAW.</div></div>',
    collaboration:
      '<div style="text-align: center; margin-bottom: 30px;"><div class="trans-bg p-10 m-t-10 m-b-10 border-radius10" style="max-width: 600px; margin-left: auto; margin-right: auto;">Welcome to the Collaboration channel. Use this space to post work in progress and invite others to collaborate. Studio One Pro+ lets you manage everything in one place with workspaces, find out how: <br /><br /><span class="btn btn-primary" id="youtubelink" data-youtube-id="WzFkWEzpiNs">Watch a Video</span></div></div>',
  };

  public notificationSubscription: any;
  public migratingPostMessage: string;
  public betaAuthorList: any;
  public filteredBetaAuthorList: any;
  public betaAuthorMap: any;
  public betaDetailsPromise: Promise<void>;
  private _betaAuthorsFor: string;
  public betaTagCountMap: any;
  public selectedChannel: any;
  public currentCommunityUrl: string;
  public restoringFromPostDetail: boolean = false;
  public displayedPostWatchers: any[] = [];

  /** Cache for community list when navigating to post detail - restored on back, cleared when navigating elsewhere */
  /** `GET /communities/:id/channels` results, keyed by community string_id. */
  private _communityChannelsCache: Record<string, CommunityChannel[]> = {};

  public communityListBackCache: {
    key: string;
    activeParentPosts: Array<any>;
    activeParentThreadMap: any;
    lastVisiblePost: any;
    lastVisibleRecentReply?: any;
    activePostsLimitReached: boolean;
    returnUrl?: string;
    algoliaState?: {
      algoliaPage: number;
      algoliaTotalPages: number;
      currentAlgoliaFilters: string;
      currentAlgoliaQuery: string;
      /** Page 1 came from the recent-activity replica, so later pages must too. */
      usingActivityIndex?: boolean;
    };
    betaTagCountMap?: any;
    filteredBetaAuthorList?: any;
    viewedPostId?: string;
  } | null = null;

  /**
   * Cross-community SEARCH state for the same picker → post-detail → picker hop.
   *
   * Separate from `communityListBackCache`: that one is consumed (and deliberately nulled)
   * by every community-view mount, whereas this survives only the one round trip back to
   * /communities. The result rows ride along rather than being re-queried so the list comes
   * back identical — Algolia can reorder between calls, and the reader is returning to a
   * specific row they want to find again.
   */
  public communitiesSearchBackCache: {
    query: string;
    results: Array<any>;
    /** The row that was opened, so the page can scroll it back into view. */
    postId: string;
  } | null = null;

  constructor(
    http: HttpClient,
    private _storageService: StorageService,
    private _windowService: WindowService,
    private _sanitizer: DomSanitizer,
    public helperService: HelperService,
    public contentService: ContentService,
    public db: AngularFirestore,
    public storageService: StorageService,
    public userService: UserService,
    public betaService: BetaService,
    public productService: ProductService,
    public fbService: FirebaseService
  ) {
    super(http, _storageService, _windowService, helperService, userService);
  }

  /**
   * Fetch the channels defined for a specific community from paeapi
   * (`GET /communities/:communityStringId/channels`). Returns an array of
   * `CommunityChannel` rows in whatever order paeapi returns them. Used by
   * `<myp-community>` for non-FSP communities in place of the legacy global
   * `GET /community/skills/` endpoint.
   *
   * FSP continues to use `getAvailableSkills()` because its channel taxonomy IS
   * the MySQL `skills` table — swapping FSP to the new endpoint would require
   * duplicating every skill row into `community_channels` first, which we
   * haven't done. DENG-727 Phase 3.
   */
  /**
   * Create a new channel on a community. Requires community-admin authorization; paeapi
   * enforces via `isCommunityAdmin`. DENG-728 Phase 4.
   */
  createCommunityChannel(communityStringId: string, args: Partial<CommunityChannel>) {
    const url = environment.paeApiUrl + "communities/" + encodeURIComponent(communityStringId) + "/channels";
    this.invalidateCommunityChannels(communityStringId);
    return this.http.post(url, JSON.stringify(args), this.helperService.getHttpOptions());
  }

  /** Update editable fields on an existing channel. */
  updateCommunityChannel(communityStringId: string, channelStringId: string, args: Partial<CommunityChannel>) {
    const url = environment.paeApiUrl + "communities/" + encodeURIComponent(communityStringId) + "/channels/" + encodeURIComponent(channelStringId);
    this.invalidateCommunityChannels(communityStringId);
    return this.http.put(url, JSON.stringify(args), this.helperService.getHttpOptions());
  }

  /** Archive (soft-delete) a channel — historical posts keep their `channels` refs. */
  deleteCommunityChannel(communityStringId: string, channelStringId: string) {
    const url = environment.paeApiUrl + "communities/" + encodeURIComponent(communityStringId) + "/channels/" + encodeURIComponent(channelStringId);
    this.invalidateCommunityChannels(communityStringId);
    return this.http.delete(url, this.helperService.getHttpOptions());
  }

  /**
   * Channels for one community, cached in memory for the session.
   *
   * The list is small, changes only when an admin edits it, and the community page refetched
   * it on every mount — including every time a member came back from a post detail, which
   * made an otherwise-instant cached back-navigation wait on a round trip.
   *
   * Mutations invalidate the entry (see `createCommunityChannel` and friends), and the admin
   * screen passes `force` so a management view never renders from a snapshot.
   */
  getCommunityChannels(communityStringId: string, force = false): Observable<CommunityChannel[]> {
    const cached = this._communityChannelsCache[communityStringId];
    if (cached && !force) {
      // Side-effects are re-applied on a cache hit too: the maps below are what other
      // components read, and skipping them would leave a cache hit subtly different from
      // a fetch.
      return of(this.applyChannelSideEffects(cached));
    }
    const url = environment.paeApiUrl + "communities/" + encodeURIComponent(communityStringId) + "/channels";
    const request$ = this.http.get<CommunityChannel[]>(url, this.helperService.getHttpOptions()).pipe(
      map((channels) => {
        this._communityChannelsCache[communityStringId] = channels;
        this.writeStoredChannels(communityStringId, channels);
        return this.applyChannelSideEffects(channels);
      })
    );
    // First visit this session: answer from the previous session's list if there is one, and
    // refresh it behind the scenes. The feed can't start until the channels are known, so this
    // took a paeapi round trip — a Lambda cold start, sometimes — off the front of every page
    // load. An admin's channel edit shows up on the next visit rather than this one.
    const stored = force ? null : this.readStoredChannels(communityStringId);
    if (stored) {
      this._communityChannelsCache[communityStringId] = stored;
      request$.subscribe({ error: () => {} });
      return of(this.applyChannelSideEffects(stored));
    }
    return request$;
  }

  private readStoredChannels(communityStringId: string): CommunityChannel[] | null {
    try {
      const list = JSON.parse(localStorage.getItem(CHANNELS_CACHE_PREFIX + communityStringId));
      return Array.isArray(list) && list.length ? list : null;
    } catch {
      return null;
    }
  }

  private writeStoredChannels(communityStringId: string, channels: CommunityChannel[]) {
    try {
      localStorage.setItem(CHANNELS_CACHE_PREFIX + communityStringId, JSON.stringify(channels || []));
    } catch {
      // Storage full or blocked: the cache is only a speed-up.
    }
  }

  /**
   * Mirror the side-effects that getAvailableSkills performs so downstream renderers
   * (comment.component's channel-tag pill, community-search's per-hit channels, etc.)
   * that look up channels via `skillsMap[stringId]` keep working for non-FSP posts.
   * Populated in ADDITION to any existing FSP entries — we don't clear because a user
   * could navigate FSP → tone-master-pro → FSP in one session and we want both maps.
   */
  private applyChannelSideEffects(channels: CommunityChannel[]): CommunityChannel[] {
    this.availableSkills = channels as any[];
    this.taggableChannels = [...channels, ...this.extraChannels.filter((item) => item.taggable)];
    this.taggableChannels.forEach((item: any) => (this.taggableChannelsMap[item.string_id] = item));
    channels.forEach((item) => {
      this.skillsMap[item.string_id] = item;
    });
    return channels;
  }

  /** Drop a community's cached channel list so the next read refetches. */
  invalidateCommunityChannels(communityStringId: string) {
    delete this._communityChannelsCache[communityStringId];
    try {
      localStorage.removeItem(CHANNELS_CACHE_PREFIX + communityStringId);
    } catch {
      // Nothing to clear.
    }
  }

  updateProfile(args) {
    return new Promise((resolve, reject) => {
      if (this.profile) {
        this.fbService
          .handleFirestorePromise(() =>
            this.db
              .collection("user_profiles")
              .doc(this.userService.user.id)
              .update({
                ...args,
              })
          )
          .then((result) => {
            this.profile = this.profile ? { ...this.profile, ...args } : args;

            satelliteDocumentUpdates();
          })
          .catch((err) => {
            reject(new Error(err?.message || "An error occurred while updating your profile."));
          });
      } else {
        this.fbService
          .handleFirestorePromise(() => this.db.collection("user_profiles").doc(this.userService.user.id).ref.get())
          .then((result) => {
            let method = "update";
            if (!result.exists) method = "set";
            // Use native Firestore API to avoid NG0203 injection context error
            this.db.firestore
              .collection("user_profiles")
              .doc(this.userService.user.id)
              [method]({
                ...args,
              })
              .then((result) => {
                this.profile = this.profile ? { ...this.profile, ...args } : args;
                if (!this.hasSetupCommunity) this.hasSetupCommunity = true;
                satelliteDocumentUpdates();
              })
              .catch((err) => {
                reject(new Error(err?.message || "An error occurred while updating your profile."));
              });
          })
          .catch((err) => {
            reject(new Error(err?.message || "An error occurred while updating your profile."));
          });
      }

      const satelliteDocumentUpdates = () => {
        if (args.firstName || args.lastName || args.photo) {
          // Use native Firestore API to avoid NG0203 injection context error
          // since this function is called from .then() callbacks
          this.db.firestore
            .collection("user_profiles")
            .doc(this.userService.user.id)
            .collection("linked_users")
            .get()
            .then((result) => {
              if (result.docs.length) {
                let promises = [];
                let updateArgs: any = {};
                if (args.firstName || args.lastName) updateArgs.name = (args.firstName || this.userService.user.firstName) + " " + (args.lastName || this.userService.user.lastName);
                if (args.photo) updateArgs.photo = args.photo;
                let batch = this.db.firestore.batch();
                result.docs.forEach((item) => {
                  batch.update(this.db.firestore.collection("user_profiles").doc(item.id).collection("linked_users").doc(this.userService.user.id), updateArgs);
                });
                batch.commit().then((result) => {
                  resolve(true);
                });
              }
            });
        } else {
          resolve(true);
        }
        resolve(true);
      };
    });
  }

  updateCommunityStatus(id, status) {
    return this.fbService.handleFirestorePromise(() => this.db.collection("user_profiles").doc(id).update({ status: status }));
  }

  /**
   * Add `stringId` to the current user's `followingCommunities` map on their
   * `user_profiles/{userId}` doc.
   *
   * Uses a nested-object `set({merge: true})` — NOT dot-notation. In Firestore's
   * compat SDK, `set()` with a dot-in-key like `{ "followingCommunities.abc": true }`
   * creates a top-level field literally named "followingCommunities.abc" instead of
   * nesting; only `update()` treats dots as field paths. `set({...}, {merge: true})`
   * with a genuinely-nested object deep-merges into the existing map, so concurrent
   * follows on different communities coexist without clobbering each other. Also
   * works when the user has no profile doc yet (unlike `update()`).
   */
  followCommunity(stringId: string): Promise<any> {
    const userId = this.userService.user?.id;
    if (!userId || !stringId) return Promise.resolve();
    return this.fbService
      .handleFirestorePromise(() =>
        // Native SDK — same NG0203 rationale as `updateProfile`'s satellite writes; the
        // AngularFirestore wrapper throws when called outside injection context, which
        // is exactly what happens when the caller resolves this from a .then() chain.
        this.db.firestore
          .collection("user_profiles")
          .doc(userId)
          .set({ followingCommunities: { [stringId]: true } }, { merge: true })
      )
      .then((result) => {
        // Re-assign `this.profile` (not just mutate in place) so the base service's
        // `set profile()` runs — it persists to `StorageKeys.COMMUNITY_PROFILE`, which is
        // what AppService reads on the next app boot. Mutating the nested map in place
        // would leave localStorage stale and the follow would look reverted on refresh.
        const nextProfile = this.profile ? { ...this.profile } : ({} as any);
        nextProfile.followingCommunities = { ...(nextProfile.followingCommunities || {}), [stringId]: true };
        this.profile = nextProfile;
        this.firebaseProfile$.next(this.profile);
        return result;
      });
  }

  /**
   * Mark this community's onboarding wizard as completed for the current user. Writes
   * two flags in one `update()` call so a mid-write crash can't leave the profile in a
   * half-state:
   *
   *   - `hasSetupCommunities.<stringId>` — the per-community flag (dot notation to keep
   *     other communities' entries in the map intact; a plain nested-object update would
   *     replace the whole map).
   *   - `hasSetupCommunity` — the legacy global flag. Still written unconditionally so
   *     any code that reads the old boolean (feed rules, admin surfaces, etc.) keeps
   *     working during the migration to per-community tracking.
   *
   * Also mirrors the write into `this.profile` so the setter persists to localStorage —
   * same rationale as `followCommunity` / `unfollowCommunity`. Otherwise a refresh would
   * re-open the wizard even though Firestore already knows it's done.
   */
  markCommunitySetupComplete(stringId: string): Promise<any> {
    const userId = this.userService.user?.id;
    if (!userId || !stringId) return Promise.resolve();
    const patch = {
      hasSetupCommunity: true,
      [`hasSetupCommunities.${stringId}`]: true,
    };
    return this.fbService
      .handleFirestorePromise(() => this.fbService.doc(`user_profiles/${userId}`).update(patch))
      .then((result) => {
        const nextProfile = this.profile ? { ...this.profile } : ({} as any);
        nextProfile.hasSetupCommunity = true;
        nextProfile.hasSetupCommunities = { ...(nextProfile.hasSetupCommunities || {}), [stringId]: true };
        this.profile = nextProfile;
        this.hasSetupCommunity = true;
        this.firebaseProfile$.next(this.profile);
        return result;
      });
  }

  /**
   * Remove `stringId` from the current user's `followingCommunities` map. Uses
   * `update()` with dot-notation so we can pass `deleteField()` to remove just that
   * key without touching other communities. `update()` treats dots as paths (the
   * inverse of `set()`, which is why the follow path uses a nested object instead).
   *
   * Delete-not-write-false so downstream readers can rely on presence == following.
   * If the doc doesn't exist, `update()` throws — swallowed here because "nothing to
   * unfollow" is a no-op.
   *
   * Uses `fbService.doc()` + `fbService.deleteField()` — same pair as the reactions
   * dual-write at ~line 1561. In firebase-js@10 the compat `.update()` rejects the
   * modular `__PRIVATE_DeleteFieldValueImpl` sentinel that `firebase.firestore.FieldValue.delete()`
   * quietly proxies to. The modular DocumentReference from `fbService.doc()` accepts it.
   */
  unfollowCommunity(stringId: string): Promise<any> {
    const userId = this.userService.user?.id;
    if (!userId || !stringId) return Promise.resolve();
    const patch = { [`followingCommunities.${stringId}`]: this.fbService.deleteField() };
    return this.fbService
      .handleFirestorePromise(() => this.fbService.doc(`user_profiles/${userId}`).update(patch))
      .then((result) => {
        if (this.profile?.followingCommunities) {
          // Same rationale as `followCommunity` — re-assign so the setter persists to
          // localStorage. Mutating in place leaves the cached snapshot stale and the
          // unfollow would appear reverted on next refresh.
          const nextProfile = { ...this.profile, followingCommunities: { ...this.profile.followingCommunities } };
          delete nextProfile.followingCommunities[stringId];
          this.profile = nextProfile;
          this.firebaseProfile$.next(this.profile);
        }
        return result;
      })
      .catch((err) => {
        // No-doc case: nothing to unfollow, keep going. Any other error re-throws.
        if (err?.code === "not-found") return null;
        throw err;
      });
  }

  /**
   * Is the current user following `stringId`? Reads from the cached profile — callers
   * that need to react to changes should subscribe to `firebaseProfile$` and re-check.
   */
  isFollowingCommunity(stringId: string): boolean {
    if (!stringId) return false;
    return !!this.profile?.followingCommunities?.[stringId];
  }

  /**
   * Read the current user's per-community CHANNEL preferences (email / mobile / desktop).
   * Firestore path: `notificationPreferences.communities[stringId]`. Falls back to all-true
   * when the user hasn't touched their prefs for this community — matches the paeapi
   * `resolveNotificationChannels` fail-open default so the UI defaults line up with what
   * the server actually sends.
   *
   * NOTE: intentionally does NOT return the legacy `notificationPreferences.community` for
   * FSP. That fallback is server-side only — showing FSP legacy prefs in the per-community
   * modal would let a user save them back over the specific FSP entry, silently converting
   * their legacy state into a locked-in per-community one. Better to render defaults and
   * let the user opt into per-community-specific values explicitly.
   */
  getCommunityChannelPrefs(stringId: string): CommunityChannelPrefs {
    // An override is a whole entry, not single keys: paeapi takes the first channel entry it
    // finds, so a partial one would mean something different there than here.
    const stored = this.profile?.notificationPreferences?.communities?.[stringId];
    if (!stored) return this.getCommunityDefaults().channels;
    return {
      email: stored.email !== false,
      mobile: stored.mobile !== false,
      desktop: stored.desktop !== false,
    };
  }

  /**
   * Read the current user's per-community TOPIC preferences (which events ping). Firestore
   * path: `communityNotificationPreferences[stringId]`. Defaults MUST mirror the paeapi
   * `notificationTopicPref.ts` DEFAULT_ALLOWED map: `new_posts: false` (quiet on broadcast),
   * everything reply/mention/reaction-oriented `true` (loud when someone engages with you).
   */
  getCommunityTopicPrefs(stringId: string): CommunityTopicPrefs {
    // Key by key, like paeapi's `isCommunityTopicAllowed`: an override for one event leaves
    // the rest following the member's defaults.
    const stored = this.profile?.communityNotificationPreferences?.[stringId] || {};
    const defaults = this.getCommunityDefaults().topics;
    const pick = (key: keyof CommunityTopicPrefs) => (typeof stored[key] === "boolean" ? stored[key] : defaults[key]);
    return {
      new_posts: pick("new_posts"),
      replies_to_me: pick("replies_to_me"),
      replies_in_my_threads: pick("replies_in_my_threads"),
      mentions: pick("mentions"),
      reactions_on_my_posts: pick("reactions_on_my_posts"),
    };
  }

  /** The member's settings for all communities, with built-ins filling anything unset. */
  getCommunityDefaults(): ResolvedCommunityDefaults {
    const stored: CommunityDefaults = this.profile?.communityDefaults || {};
    const topics = stored.topics || {};
    const channels = stored.channels;
    return {
      channels: channels ? { email: channels.email !== false, mobile: channels.mobile !== false, desktop: channels.desktop !== false } : { ...BUILT_IN_CHANNELS },
      topics: {
        new_posts: typeof topics.new_posts === "boolean" ? topics.new_posts : BUILT_IN_TOPICS.new_posts,
        replies_to_me: typeof topics.replies_to_me === "boolean" ? topics.replies_to_me : BUILT_IN_TOPICS.replies_to_me,
        replies_in_my_threads: typeof topics.replies_in_my_threads === "boolean" ? topics.replies_in_my_threads : BUILT_IN_TOPICS.replies_in_my_threads,
        mentions: typeof topics.mentions === "boolean" ? topics.mentions : BUILT_IN_TOPICS.mentions,
        reactions_on_my_posts: typeof topics.reactions_on_my_posts === "boolean" ? topics.reactions_on_my_posts : BUILT_IN_TOPICS.reactions_on_my_posts,
      },
      sortMode: stored.sortMode === "date" ? "date" : "recentactivity",
      viewMode: stored.viewMode === "detailed" ? "detailed" : "list",
      startView: stored.startView === "all" ? "all" : "favorites",
    };
  }

  /**
   * Save the member's settings for all communities. Merged into `communityDefaults`, so a
   * caller can send just what it changed. Communities with an override keep it.
   */
  updateCommunityDefaults(patch: CommunityDefaults): Promise<any> {
    const userId = this.userService.user?.id;
    const nextProfile = this.profile ? { ...this.profile } : ({} as any);
    nextProfile.communityDefaults = { ...(nextProfile.communityDefaults || {}), ...patch };
    this.profile = nextProfile;
    this.firebaseProfile$.next(this.profile);
    if (patch.sortMode) this.communitySortModeChanged$.next({ stringId: null, mode: patch.sortMode });
    if (!userId) return Promise.resolve();
    return this.fbService.handleFirestorePromise(() => this.db.firestore.collection("user_profiles").doc(userId).set({ communityDefaults: patch }, { merge: true }));
  }

  // --- Favorite channels -------------------------------------------------------------
  //
  // `favoriteChannels[communityStringId][channelStringId] = epoch millis starred`. Nested by
  // community because channel string_ids repeat across communities (two communities can each
  // have a `feature-suggestions`). The time orders the hub's list, oldest first.

  /** The member's starred channels, in the order they were starred. */
  getFavoriteChannels(): Array<{ communityStringId: string; channelStringId: string; at: number }> {
    const map = this.profile?.favoriteChannels || {};
    const rows: Array<{ communityStringId: string; channelStringId: string; at: number }> = [];
    Object.keys(map).forEach((communityStringId) => {
      Object.keys(map[communityStringId] || {}).forEach((channelStringId) => {
        const at = Number(map[communityStringId][channelStringId]);
        if (at) rows.push({ communityStringId, channelStringId, at });
      });
    });
    return rows.sort((a, b) => a.at - b.at);
  }

  isFavoriteChannel(communityStringId: string, channelStringId: string): boolean {
    return !!this.profile?.favoriteChannels?.[communityStringId]?.[channelStringId];
  }

  /**
   * Star or unstar a channel. In memory first so the rail updates at once; the write is a
   * dot-path `update()`, the call that accepts `deleteField()` for unstarring.
   *
   * Starring a channel in a community the member hasn't joined joins it too: a star is a
   * stronger signal than a follow, and a starred channel in an unfollowed community would sit
   * in the rail with none of its posts in their feed. Every star surface goes through here, so
   * the rule holds everywhere. The join is written first, which also creates the profile doc
   * that the star's `update()` needs. Same rule as ns-myfender's `CommunityHubService`.
   */
  setFavoriteChannel(communityStringId: string, channelStringId: string, favorite: boolean): Promise<any> {
    if (!communityStringId || !channelStringId) return Promise.resolve();
    const join = favorite && !this.profile?.followingCommunities?.[communityStringId];
    const at = Date.now();
    const nextProfile = this.profile ? { ...this.profile } : ({} as any);
    const all = { ...(nextProfile.favoriteChannels || {}) };
    const forCommunity = { ...(all[communityStringId] || {}) };
    if (favorite) forCommunity[channelStringId] = at;
    else delete forCommunity[channelStringId];
    if (Object.keys(forCommunity).length) all[communityStringId] = forCommunity;
    else delete all[communityStringId];
    nextProfile.favoriteChannels = all;
    this.profile = nextProfile;
    this.firebaseProfile$.next(this.profile);

    const userId = this.userService.user?.id;
    if (!userId) return Promise.resolve();
    return (join ? this.followCommunity(communityStringId) : Promise.resolve()).then(() =>
      this.fbService.handleFirestorePromise(() =>
        this.fbService.doc(`user_profiles/${userId}`).update({ [`favoriteChannels.${communityStringId}.${channelStringId}`]: favorite ? at : this.fbService.deleteField() })
      )
    );
  }

  /** Whether this community has any setting of its own, rather than following the defaults. */
  hasCommunityOverrides(stringId: string): boolean {
    const p = this.profile || ({} as any);
    return !!(
      p.notificationPreferences?.communities?.[stringId] ||
      (p.communityNotificationPreferences?.[stringId] && Object.keys(p.communityNotificationPreferences[stringId]).length) ||
      p.communitySortModes?.[stringId]
    );
  }

  /**
   * Save one community's settings as overrides of the member's defaults: only what differs
   * is stored, and anything that now matches the defaults is removed. That keeps "change it
   * once for all communities" working for every community that never diverged — saving the
   * modal used to write every key, freezing the defaults of the day into each community.
   *
   * One `update()` with dot paths, the pair that accepts `deleteField()` (see
   * `updateCommunitySignature`).
   */
  saveCommunityOverrides(stringId: string, settings: { channels: CommunityChannelPrefs; topics: CommunityTopicPrefs; sortMode: "recentactivity" | "date" }): Promise<any> {
    const userId = this.userService.user?.id;
    if (!stringId) return Promise.resolve();
    const defaults = this.getCommunityDefaults();
    const del = this.fbService.deleteField();

    const channelsDiffer = (["email", "mobile", "desktop"] as const).some((k) => settings.channels[k] !== defaults.channels[k]);
    const topicOverride: Partial<CommunityTopicPrefs> = {};
    (Object.keys(BUILT_IN_TOPICS) as Array<keyof CommunityTopicPrefs>).forEach((k) => {
      if (settings.topics[k] !== defaults.topics[k]) topicOverride[k] = settings.topics[k];
    });
    const hasTopicOverride = Object.keys(topicOverride).length > 0;
    const sortDiffers = settings.sortMode !== defaults.sortMode;

    const patch: any = {
      [`notificationPreferences.communities.${stringId}`]: channelsDiffer ? { ...settings.channels } : del,
      [`communityNotificationPreferences.${stringId}`]: hasTopicOverride ? topicOverride : del,
      [`communitySortModes.${stringId}`]: sortDiffers ? settings.sortMode : del,
    };

    const wasSort = this.getCommunitySortMode(stringId);
    const nextProfile = this.profile ? { ...this.profile } : ({} as any);
    const channelMap = { ...(nextProfile.notificationPreferences?.communities || {}) };
    if (channelsDiffer) channelMap[stringId] = { ...settings.channels };
    else delete channelMap[stringId];
    nextProfile.notificationPreferences = { ...(nextProfile.notificationPreferences || {}), communities: channelMap };
    const topicMap = { ...(nextProfile.communityNotificationPreferences || {}) };
    if (hasTopicOverride) topicMap[stringId] = topicOverride;
    else delete topicMap[stringId];
    nextProfile.communityNotificationPreferences = topicMap;
    const sortMap = { ...(nextProfile.communitySortModes || {}) };
    if (sortDiffers) sortMap[stringId] = settings.sortMode;
    else delete sortMap[stringId];
    nextProfile.communitySortModes = sortMap;
    this.profile = nextProfile;
    this.firebaseProfile$.next(this.profile);
    if (wasSort !== settings.sortMode) this.communitySortModeChanged$.next({ stringId, mode: settings.sortMode });

    if (!userId) return Promise.resolve();
    return this.fbService.handleFirestorePromise(() => this.fbService.doc(`user_profiles/${userId}`).update(patch));
  }

  /** Drop every override for this community, so it follows the member's defaults again. */
  resetCommunitySettings(stringId: string): Promise<any> {
    const defaults = this.getCommunityDefaults();
    return this.saveCommunityOverrides(stringId, { channels: defaults.channels, topics: defaults.topics, sortMode: defaults.sortMode });
  }

  /**
   * Write the current user's per-community CHANNEL preferences. Uses dot-notation
   * `update()` so concurrent toggles on different communities don't clobber each other
   * (a plain `set` on the whole `communities` map would replace it). Mirrors into the
   * cached profile and re-assigns `this.profile` so the setter persists to localStorage
   * — same rationale as `followCommunity`.
   */
  updateCommunityChannelPrefs(stringId: string, prefs: CommunityChannelPrefs): Promise<any> {
    const userId = this.userService.user?.id;
    if (!userId || !stringId) return Promise.resolve();
    // set-merge with a nested object — same pattern as `followCommunity`. `update()` with
    // dot-notation on THREE separate keys would work too but is verbose; a nested set-merge
    // combines them into one write while still preserving other communities' entries in
    // the map (Firestore deep-merges nested objects on merge:true).
    return this.fbService
      .handleFirestorePromise(() =>
        this.db.firestore
          .collection("user_profiles")
          .doc(userId)
          .set({ notificationPreferences: { communities: { [stringId]: prefs } } }, { merge: true })
      )
      .then((result) => {
        const nextProfile = this.profile ? { ...this.profile } : ({} as any);
        nextProfile.notificationPreferences = { ...(nextProfile.notificationPreferences || {}) };
        nextProfile.notificationPreferences.communities = { ...(nextProfile.notificationPreferences.communities || {}), [stringId]: prefs };
        this.profile = nextProfile;
        this.firebaseProfile$.next(this.profile);
        return result;
      });
  }

  /**
   * Write the current user's per-community TOPIC preferences. Same nested-set-merge shape
   * as the channel writer; keeps other communities' topic-pref entries intact.
   */
  updateCommunityTopicPrefs(stringId: string, prefs: CommunityTopicPrefs): Promise<any> {
    const userId = this.userService.user?.id;
    if (!userId || !stringId) return Promise.resolve();
    return this.fbService
      .handleFirestorePromise(() =>
        this.db.firestore
          .collection("user_profiles")
          .doc(userId)
          .set({ communityNotificationPreferences: { [stringId]: prefs } }, { merge: true })
      )
      .then((result) => {
        const nextProfile = this.profile ? { ...this.profile } : ({} as any);
        nextProfile.communityNotificationPreferences = { ...(nextProfile.communityNotificationPreferences || {}), [stringId]: prefs };
        this.profile = nextProfile;
        this.firebaseProfile$.next(this.profile);
        return result;
      });
  }

  /**
   * The signature used in one community (DENG-847), or "" when there is none. Resolved in
   * order:
   *
   *   1. `communitySignatures[stringId]` — this community's own signature.
   *   2. `defaultCommunitySignature` — the member's signature for every community without
   *      its own. Stored as "" (not deleted) when they clear it, so "cleared" and "never
   *      set" stay distinguishable.
   *   3. Never set a default at all (signatures saved before the default existed): their
   *      first per-community signature, so a member who set one somewhere has it
   *      everywhere without re-saving.
   *
   * Plain text by design. It is appended to the post body as text, so anything that looks
   * like markup lands in the post as the characters the member typed.
   */
  getCommunitySignature(stringId: string): string {
    const own = this.getOwnCommunitySignature(stringId);
    if (own) return own;
    return this.getDefaultCommunitySignature();
  }

  /**
   * The signature for every community without its own — steps 2 and 3 of
   * `getCommunitySignature`. What the all-communities settings edit.
   */
  getDefaultCommunitySignature(): string {
    const fallback = this.profile?.defaultCommunitySignature;
    if (typeof fallback === "string") return fallback;
    const signatures = this.profile?.communitySignatures || {};
    const firstKey = Object.keys(signatures)
      .sort()
      .find((key) => signatures[key]);
    return firstKey ? signatures[firstKey] : "";
  }

  /** This community's own signature, ignoring the all-communities default — "" when it has none. */
  getOwnCommunitySignature(stringId: string): string {
    return this.profile?.communitySignatures?.[stringId] || "";
  }

  /**
   * Write the signature used in every community that has none of its own. Stored verbatim
   * like `updateCommunitySignature`; a blank value is stored as "" rather than deleted (see
   * `getCommunitySignature` step 2), which also keeps it on the plain set-merge path.
   */
  updateDefaultCommunitySignature(signature: string): Promise<any> {
    const userId = this.userService.user?.id;
    if (!userId) return Promise.resolve();
    const raw = signature || "";
    const value = raw.trim().length > 0 ? raw : "";
    return this.fbService
      .handleFirestorePromise(() => this.db.firestore.collection("user_profiles").doc(userId).set({ defaultCommunitySignature: value }, { merge: true }))
      .then((result) => {
        this.profile = { ...(this.profile || ({} as any)), defaultCommunitySignature: value };
        this.firebaseProfile$.next(this.profile);
        return result;
      });
  }

  /**
   * Write (or clear) the signature for one community. A blank value deletes the entry
   * rather than storing "" — `getCommunitySignature` treats both the same, but leaving
   * blanks behind makes the stored doc harder to read.
   *
   * Stored VERBATIM, whitespace included. Leading/trailing newlines are how a member
   * controls the gap between their post and their sign-off, so they survive the round trip.
   *
   * Writing uses a nested set-merge so other communities' signatures survive. CLEARING
   * cannot: the compat `set()` rejects the modular delete sentinel outright —
   *
   *   Unsupported field value: a custom __PRIVATE_DeleteFieldValueImpl object
   *
   * — which made saving an empty signature fail, so nobody could remove one. Clearing
   * therefore goes the same way `unfollowCommunity` does: `fbService.doc()` + `update()`
   * with a dot-notation path, which is the pair that accepts `deleteField()`. Note the
   * inversion — `update()` reads dots as paths, `set()` does not, which is why the two
   * branches are shaped differently.
   */
  updateCommunitySignature(stringId: string, signature: string): Promise<any> {
    const userId = this.userService.user?.id;
    if (!userId || !stringId) return Promise.resolve();
    const raw = signature || "";
    // `trim()` decides whether the member cleared the field — it never rewrites what they
    // typed. Leading and trailing newlines are deliberate spacing (a blank line between the
    // post and the sign-off), and trimming them silently discarded that.
    const hasContent = raw.trim().length > 0;
    return this.fbService
      .handleFirestorePromise(() =>
        hasContent
          ? this.db.firestore
              .collection("user_profiles")
              .doc(userId)
              .set({ communitySignatures: { [stringId]: raw } }, { merge: true })
          : // `update()` throws when the document has no such field yet; clearing a
            // signature that was never set is a no-op, not an error.
            this.fbService
              .doc(`user_profiles/${userId}`)
              .update({ [`communitySignatures.${stringId}`]: this.fbService.deleteField() })
              .catch(() => undefined)
      )
      .then((result) => {
        const nextProfile = this.profile ? { ...this.profile } : ({} as any);
        const signatures = { ...(nextProfile.communitySignatures || {}) };
        if (hasContent) signatures[stringId] = raw;
        else delete signatures[stringId];
        nextProfile.communitySignatures = signatures;
        this.profile = nextProfile;
        this.firebaseProfile$.next(this.profile);
        return result;
      });
  }

  // --- Legacy channel marks ------------------------------------------------------------
  //
  // `communityLastSeen[communityStringId][channelId] = epoch millis`: when the member last
  // opened each channel. The badges used to count against these; they now count per thread,
  // like the feed's rows (see `getCommunityUnreadCandidates`), and nothing writes them any more.
  // They are read once more, to seed a member's read floor (`postUnreadFloor`) from their
  // oldest mark so moving models didn't hand them a month of backlog.

  /** Every channel mark for one community, `{ [channelId]: epochMillis }`. */
  getCommunityLastSeen(stringId: string): Record<string, number> {
    return this.profile?.communityLastSeen?.[stringId] || {};
  }

  // --- When the member last opened a post ----------------------------------------------
  //
  // `community_post_views/{userId}/posts/{postId}` = `{ at: epochMillis }`. Per post, where
  // `communityLastSeen` above is per channel: the post detail uses it to decide where to
  // open (top on a first visit, else the first reply since your last one) and where to draw
  // "New replies". Its own top-level collection so it can be owner-only — see the rule in
  // paeapi/firestore.rules for why it can't live under `user_profiles`.

  /**
   * In-memory copy of the reader's view records, `postId → at` (0 = known never opened), so
   * the post detail can decide where to open in the same frame as the click instead of after
   * a round trip. Filled in bulk by `prefetchPostViews` as feed pages load, and kept current by
   * `markPostViewed`. Scoped to one user; a different signed-in user starts it over.
   */
  private _postViews = new Map<string, number>();
  /**
   * Fires when view records land (a prefetch chunk) or change (`markPostViewed`), and when
   * the read floor moves — the feed recomputes its unread rows on it.
   */
  public postViews$ = new Subject<void>();
  /** A post's `pins` (saves) changed locally — see `pinUnpinPost`. Views holding their own copy of the post sync from this. */
  public postPinsChanged$ = new Subject<{ id: string; pins: string[] }>();
  /**
   * A post went into or out of a community's Featured rail (DENG-1294). The rail reloads, and
   * the feed drops the post — or takes it back — without a refetch.
   */
  public featuredChanged$ = new Subject<{ communityStringId: string; post: any; featured: boolean }>();
  /** A community's feed ordering changed locally — see `setCommunitySortMode`. The open feed reloads on it. */
  /** `stringId` null = the default for all communities changed (`updateCommunityDefaults`). */
  public communitySortModeChanged$ = new Subject<{ stringId: string | null; mode: "recentactivity" | "date" }>();
  /** Reads in flight, so a click on a post mid-prefetch waits for that read rather than issuing its own. */
  private _postViewsInflight = new Map<string, Promise<number>>();
  private _postViewsUserId: string | null = null;

  private _postViewsRef(userId: string) {
    return this.db.firestore.collection("community_post_views").doc(String(userId)).collection("posts");
  }

  /** The cache for the signed-in user — emptied when that changes. Null when signed out. */
  private _postViewsFor(): string | null {
    const userId = this.userService.user?.id ? String(this.userService.user.id) : null;
    if (userId !== this._postViewsUserId) {
      this._postViews.clear();
      this._postViewsInflight.clear();
      this._postViewsUserId = userId;
    }
    return userId;
  }

  /**
   * Synchronous look-up: when the member last opened `postId` (0 = never), or `undefined` when
   * that isn't known yet and a read is needed. Signed out is "never".
   */
  peekPostLastViewed(postId: string): number | undefined {
    if (!this._postViewsFor()) return 0;
    return this._postViews.get(String(postId));
  }

  /**
   * Load the view records for a batch of posts — the feed calls this as each page arrives, so
   * that by the time a row is clicked the answer is already in memory. Skips ids already known
   * or in flight; one query per 30 ids (Firestore's `in` limit). Best-effort: a failed chunk
   * just leaves those posts to the per-click read.
   */
  prefetchPostViews(postIds: Array<string>): Promise<void> {
    const userId = this._postViewsFor();
    if (!userId || !postIds?.length) return Promise.resolve();
    const wanted = Array.from(new Set(postIds.map((id) => String(id || "")).filter((id) => id && !this._postViews.has(id) && !this._postViewsInflight.has(id))));
    if (!wanted.length) return Promise.resolve();

    const chunks: string[][] = [];
    for (let i = 0; i < wanted.length; i += 30) chunks.push(wanted.slice(i, i + 30));
    return Promise.all(
      chunks.map((ids) => {
        const query = this.fbService
          .handleFirestorePromise(() => this._postViewsRef(userId).where(firebase.firestore.FieldPath.documentId(), "in", ids).get())
          .then((snap: any) => {
            // Signed out or switched user while the query was out — don't pollute the new cache.
            if (this._postViewsUserId !== userId) return;
            const found = new Map<string, number>();
            (snap?.docs || []).forEach((doc: any) => found.set(doc.id, Number(doc.data()?.at) || 0));
            // Absent from the result means never opened — cache that too, it's what makes a
            // first visit instant as well.
            ids.forEach((id) => {
              if (!this._postViews.has(id)) this._postViews.set(id, found.get(id) || 0);
            });
            this.postViews$.next();
          })
          .catch((err) => console.warn("[community] prefetchPostViews failed", err))
          .finally(() => ids.forEach((id) => this._postViewsInflight.delete(id)));
        ids.forEach((id) =>
          this._postViewsInflight.set(
            id,
            query.then(() => this._postViews.get(id) ?? 0)
          )
        );
        return query;
      })
    ).then(() => undefined);
  }

  /**
   * Every feed load funnels through here — first page, both "Show more" paths (Firestore and
   * Algolia), and the rest. (A hook on the channel's `postsLoaded` only ever warmed page one:
   * the Firestore "Show more" never emits it.) Warm the view records for the parent posts in this batch as the data lands, so
   * opening any of them can place "New replies" with no read.
   */
  processPostsAndReplies(posts, clear?, storageKey?, channelId?: string, betaStringId?: string) {
    const result = super.processPostsAndReplies(posts, clear, storageKey, channelId, betaStringId);
    const parentIds = (posts || []).filter((post: any) => post && !post.parentID).map((post: any) => post.id || post.key);
    if (parentIds.length) this.prefetchPostViews(parentIds);
    return result;
  }

  /** When the member last opened `postId`, or 0 for never (or signed out, or on error). Cache first. */
  getPostLastViewed(postId: string): Promise<number> {
    const userId = this._postViewsFor();
    if (!userId || !postId) return Promise.resolve(0);
    const id = String(postId);
    if (this._postViews.has(id)) return Promise.resolve(this._postViews.get(id));
    const inflight = this._postViewsInflight.get(id);
    if (inflight) return inflight;
    return this.fbService
      .handleFirestorePromise(() => this._postViewsRef(userId).doc(id).get())
      .then((snap: any) => {
        const at = Number(snap?.data?.()?.at) || 0;
        if (this._postViewsUserId === userId && !this._postViews.has(id)) this._postViews.set(id, at);
        return at;
      })
      .catch(() => 0);
  }

  /** Record that the member has seen `postId` up to `at`. Updates the cache at once; the write is fire-and-forget. */
  markPostViewed(postId: string, at: number = Date.now()): Promise<any> {
    const userId = this._postViewsFor();
    if (!userId || !postId) return Promise.resolve();
    this._postViews.set(String(postId), at);
    this.postViews$.next();
    return this.fbService.handleFirestorePromise(() => this._postViewsRef(userId).doc(String(postId)).set({ at })).catch((err) => console.warn("[community] markPostViewed failed", err));
  }

  // --- Unread posts: the read floor ---------------------------------------------------
  //
  // A feed row is unread when its thread has activity from someone else newer than BOTH your
  // last visit to that post (`community_post_views`, above) and this per-community floor.
  // The floor is what stops "never opened" meaning "unread" forever:
  //  - seeded on first contact, so a new member doesn't land on a wall of unread;
  //  - "Mark all read" moves it to now;
  //  - never earlier than the 30-day lookback the channel counts use.
  // `user_profiles/{uid}.communityReadFloor[community]` — beside `communityLastSeen`.
  // The badges count by the same rule (`getCommunityUnreadCandidates`), so a row and its
  // channel's count always agree.

  /** The raw floor for a community, or 0 if there isn't one yet. */
  getCommunityReadFloor(stringId: string): number {
    return Number(this.profile?.communityReadFloor?.[stringId]) || 0;
  }

  /**
   * Move the floor forward to `at` (monotonic — it never moves back). Mirrors into the
   * in-memory profile at once, then writes.
   */
  setCommunityReadFloor(stringId: string, at: number = Date.now(), notify = true): Promise<any> {
    const userId = this.userService.user?.id;
    if (!userId || !stringId || !this.profile) return Promise.resolve();
    if (this.getCommunityReadFloor(stringId) >= at) return Promise.resolve();
    const nextProfile = { ...this.profile } as any;
    nextProfile.communityReadFloor = { ...(nextProfile.communityReadFloor || {}), [stringId]: at };
    this.profile = nextProfile;
    this.firebaseProfile$.next(this.profile);
    if (notify) this.postViews$.next();
    return this.fbService
      .handleFirestorePromise(() =>
        this.db.firestore
          .collection("user_profiles")
          .doc(String(userId))
          .set({ communityReadFloor: { [stringId]: at } }, { merge: true })
      )
      .catch((err) => console.warn("[community] setCommunityReadFloor failed", err));
  }

  /**
   * The cutoff a feed row's activity must beat to count as unread, or 0 for "highlight
   * nothing" (signed out, or the profile hasn't loaded so the floor can't be known yet — the
   * feed recomputes when it does).
   *
   * No floor yet is first contact with per-post unread. A member who already has channel
   * marks gets their OLDEST mark, so switching models doesn't blank their unread rows; a
   * brand-new member gets now.
   */
  /**
   * When a thread last had activity from SOMEONE ELSE: the post itself unless you wrote it,
   * and its newest reply not by you. Your own activity never makes a thread unread — a thread
   * whose newest reply is yours has nothing in it you haven't seen.
   *
   * Walks whatever replies are loaded (`activeParentThreadMap`), and also reads the parent's
   * stored summary — in the list layout the replies usually aren't loaded at all. The summary
   * names only the NEWEST reply's author, which is exactly enough: if that reply is someone
   * else's it is the answer, and if it is yours, nothing newer exists.
   *
   * With `postUnreadFloor` and the view records (`peekPostLastViewed`), what makes a feed row
   * unread — on the community page and in the Communities hub alike.
   */
  postActivityMillis(post: any): number {
    const me = this.userService.user?.id;
    const byMe = (item: any) => !!me && `${item?.author?.id}` === `${me}`;
    const millis = (value: any): number => {
      if (!value) return 0;
      if (typeof value.toDate === "function") return value.toDate().getTime();
      if (value instanceof Date) return value.getTime();
      return this._epochMillis(value);
    };
    let latest = byMe(post) ? 0 : millis(post?.created);
    const replies = this.activeParentThreadMap?.[post?.key] || this.activeParentThreadMap?.[post?.id] || [];
    replies.forEach((reply: any) => {
      if (byMe(reply)) return;
      const at = millis(reply?.created);
      if (at > latest) latest = at;
    });
    const lastReplyAt = millis(post?.last_reply_at);
    if (lastReplyAt > latest && !(me && `${post?.last_reply_author_id}` === `${me}`)) latest = lastReplyAt;
    return latest;
  }

  postUnreadFloor(stringId: string): number {
    if (!this.userService.user?.id || !stringId || !this.profile) return 0;
    let floor = this.getCommunityReadFloor(stringId);
    if (!floor) {
      const marks = Object.values(this.getCommunityLastSeen(stringId))
        .map(Number)
        .filter((n) => n > 0);
      floor = marks.length ? Math.min(...marks) : Date.now();
      // Quiet: this runs inside the feed's own recompute, which is already using `floor`.
      this.setCommunityReadFloor(stringId, floor, false);
    }
    return Math.max(floor, Date.now() - this.UNREAD_LOOKBACK_MS);
  }

  // --- Which channels have something new ----------------------------------------------

  /**
   * Newest posts one "what's new" query will look at.
   *
   * Someone returning after six months would otherwise pull thousands of rows to be told
   * "lots is new" — which the first 300 already tell them. The index this queries is the
   * `created desc` replica, so these are genuinely the newest.
   */
  private readonly UNREAD_MAX_HITS = 300;
  /** Nothing older than this counts as new, however long ago the member last looked. */
  private readonly UNREAD_LOOKBACK_MS = 30 * 24 * 60 * 60 * 1000;
  /**
   * How many of your own / pinned threads the My Posts and My Pinned Posts counts consider.
   *
   * Unlike the activity window this is not time-bounded — an old thread of yours can still
   * get a reply today — so it needs a ceiling of its own. Past this, replies to your oldest
   * threads stop being counted.
   */
  private readonly UNREAD_OWNED_POST_CAP = 500;

  private _algoliaClient: SearchClient;
  private get algoliaClient(): SearchClient {
    if (!this._algoliaClient) this._algoliaClient = searchClient(environment.algolia.app_id, environment.algolia.apiKey);
    return this._algoliaClient;
  }

  /**
   * Firestore Timestamps reach Algolia as epoch numbers, but some records carry seconds and
   * some milliseconds. Same rule `initPostsFromAlgolia`'s `wrapTimestamp` uses.
   */
  private _epochMillis(value: any): number {
    const num = Number(value) || 0;
    if (!num) return 0;
    return num < 1e12 ? num * 1000 : num;
  }

  /**
   * The threads with someone else's activity since the community's read floor, bucketed by
   * channel — what the badges are counted from (`summarizeCommunityUnread`). Threads, not
   * items: a thread with four new replies is one row in the feed and one thing to go and read.
   *
   * Per post, not per channel (see `community-unread.utils`): a thread drops off every badge it
   * is on once you open it, and opening a channel clears nothing. So this loads the view records
   * for the threads it found before resolving, and the counts are re-summarized — no query — as
   * those records change.
   *
   * One Algolia round trip per community: the activity window (posts AND replies, which carry
   * their parent's `channels`), plus the parent ids behind My Posts and Saved Posts. The window
   * takes the newest `UNREAD_MAX_HITS`, so past that the counts understate — render them with a
   * ceiling. Your own posts and replies are excluded by the query; deleted and scheduled ones by
   * the walk.
   *
   * Fails closed: a rejected query, or no floor yet (signed out, profile not loaded), resolves
   * to null and the badges show nothing rather than something wrong.
   */
  getCommunityUnreadCandidates(stringId: string, channelIds: Array<string>): Promise<UnreadCandidates | null> {
    const userId = this.userService.user?.id;
    const floor = this.postUnreadFloor(stringId);
    // All Posts shows everything and carries no badge of its own.
    const shown = (channelIds || []).map((id) => (id ? String(id) : "")).filter((id) => id && id !== "all_posts");
    if (!stringId || !userId || !floor || !shown.length) return Promise.resolve(null);

    const idRequest = (clause: string) => ({
      indexName: environment.communityAlgoliaIndex,
      query: "",
      filters: [`community_string_id:"${stringId}"`, "type:post", clause].join(" AND "),
      hitsPerPage: this.UNREAD_OWNED_POST_CAP,
      attributesToRetrieve: [] as string[],
      relevancyStrictness: 0,
    });
    // `parentID` isn't filterable and a reply carries nothing about its parent's author, so
    // My Posts / Saved Posts are matched against these id sets in the walk. Not time-bounded: a
    // reply today on a thread you wrote two years ago is still a reply to your thread.
    const wantsMine = shown.includes(UNREAD_MY_POSTS_VIEW);
    const wantsSaved = shown.includes(UNREAD_SAVED_POSTS_VIEW);

    return this.algoliaClient
      .search({
        requests: [
          {
            indexName: environment.communityAlgoliaIndex,
            query: "",
            filters: [`community_string_id:"${stringId}"`, `NOT author.id:"${userId}"`].join(" AND "),
            numericFilters: [`created>${floor}`],
            hitsPerPage: this.UNREAD_MAX_HITS,
            // Everything the walk needs and nothing else — this runs on page load.
            attributesToRetrieve: ["channels", "created", "deleted", "scheduled_date", "parentID"],
            relevancyStrictness: 0,
          },
          ...(wantsMine ? [idRequest(`author.id:"${userId}"`)] : []),
          ...(wantsSaved ? [idRequest(`pins:"${userId}"`)] : []),
        ],
      })
      .then((res: any) => {
        const results = res?.results || [];
        const idsOf = (result: any) => new Set<string>((result?.hits || []).map((hit: any) => String(hit.objectID)));
        const candidates = collectUnreadCandidates(results[0]?.hits || [], {
          channelIds: shown,
          myPostIds: wantsMine ? idsOf(results[1]) : undefined,
          savedPostIds: wantsSaved ? idsOf(results[wantsMine ? 2 : 1]) : undefined,
        });
        // Their view records, so the first count already leaves out what you've read. Most
        // were warmed by the feed; the rest cost one `in` query per 30.
        return this.prefetchPostViews(unreadCandidateThreadIds(candidates)).then(() => candidates);
      })
      .catch(() => null);
  }

  /** The counts as they stand now: `candidates` against the read floor and your view records. Cheap — no reads. */
  summarizeCommunityUnread(stringId: string, candidates: UnreadCandidates | null): UnreadSummary {
    return summarizeUnread(candidates, this.postUnreadFloor(stringId), (id) => this.peekPostLastViewed(id));
  }

  /** Unread threads per channel — see `getCommunityUnreadCandidates`. */
  getChannelUnreadCounts(stringId: string, channelIds: Array<string>): Promise<Record<string, number>> {
    return this.getCommunityUnreadSummary(stringId, channelIds).then((summary) => summary.channels);
  }

  /** Candidates and counts in one go, for a caller that doesn't re-summarize as you read. */
  getCommunityUnreadSummary(stringId: string, channelIds: Array<string>): Promise<UnreadSummary> {
    return this.getCommunityUnreadCandidates(stringId, channelIds).then((candidates) => this.summarizeCommunityUnread(stringId, candidates));
  }

  /**
   * Feed ordering for one community: "recentactivity" puts the thread with the newest reply
   * on top, "date" orders by when each thread was posted.
   *
   * Defaults to recent activity. A community feed is a conversation — a question answered
   * this morning matters more than one posted this morning and ignored — and the list row
   * shows "Latest activity" as its timestamp, which only reads correctly in this order.
   *
   * In the Firestore profile with the other per-community preferences, so it follows the
   * member between devices.
   */
  getCommunitySortMode(stringId: string): "recentactivity" | "date" {
    const override = this.profile?.communitySortModes?.[stringId];
    if (override === "date" || override === "recentactivity") return override;
    return this.getCommunityDefaults().sortMode;
  }

  /** Persist the feed ordering for one community, leaving every other community's alone. */
  setCommunitySortMode(stringId: string, mode: "recentactivity" | "date"): Promise<any> {
    const userId = this.userService.user?.id;
    if (!stringId) return Promise.resolve();

    // In memory first so the feed can reload immediately; the write is a round trip.
    const changed = this.getCommunitySortMode(stringId) !== mode;
    const nextProfile = this.profile ? { ...this.profile } : ({} as any);
    nextProfile.communitySortModes = { ...(nextProfile.communitySortModes || {}), [stringId]: mode };
    this.profile = nextProfile;
    this.firebaseProfile$.next(this.profile);
    if (changed) this.communitySortModeChanged$.next({ stringId, mode });

    if (!userId) return Promise.resolve();
    return this.fbService.handleFirestorePromise(() =>
      this.db.firestore
        .collection("user_profiles")
        .doc(userId)
        .set({ communitySortModes: { [stringId]: mode } }, { merge: true })
    );
  }

  /**
   * Feed layout for one community — "detailed" (full `<myp-comment>` posts) or "list" (the
   * compact mobile-style rows).
   *
   * Kept in localStorage rather than the Firestore profile, unlike signatures and notification
   * prefs: this is device-local view state, and a Firestore write per toggle-click would be
   * absurd. Mirrors how beta stores its own view mode in `BETA_FILTER_SETTINGS`.
   *
   * Defaults to "list" — the scannable feed plus the split-pane detail is now the intended
   * experience, so only an explicit "detailed" opts out. Anything unrecognised in storage
   * falls back to the default rather than rendering nothing.
   */
  getCommunityViewMode(stringId: string): "list" | "detailed" {
    // Global now (see `CommunityDefaults`): with every community in one feed, the layout is a
    // choice about reading, not about a community. A per-community value saved before that
    // still applies until the member next switches layout.
    const global = this.profile?.communityDefaults?.viewMode;
    if (global === "detailed" || global === "list") return global;
    const stored = this.profile?.communityViewModes?.[stringId];
    if (stored === "detailed" || stored === "list") return stored;
    // Fall back to the localStorage this used to live in, so nobody's existing choice is
    // silently reset by the move. The next toggle writes it to the profile and it stops
    // mattering; the read stays because there is no migration pass.
    const legacy = this.storageService.getItem(StorageKeys.COMMUNITY_VIEW_SETTINGS) || {};
    return legacy[stringId] === "detailed" ? "detailed" : "list";
  }

  /**
   * Width of the split-pane post detail, in px. Not per community: the member is expressing
   * a preference about their screen, not about a community, so dragging it once should hold
   * everywhere. 0 means "never set" — the caller seeds half the row.
   */
  getCommunityPaneWidth(): number {
    const stored = this.storageService.getItem(StorageKeys.COMMUNITY_VIEW_SETTINGS) || {};
    const width = Number(stored.paneWidth);
    return Number.isFinite(width) && width > 0 ? width : 0;
  }

  setCommunityPaneWidth(width: number) {
    const stored = this.storageService.getItem(StorageKeys.COMMUNITY_VIEW_SETTINGS) || {};
    stored.paneWidth = Math.round(width);
    this.storageService.setItem(StorageKeys.COMMUNITY_VIEW_SETTINGS, stored);
  }

  /**
   * Persist the feed layout for one community, leaving every other community's untouched.
   *
   * In the Firestore profile rather than localStorage since 2026-09-21, alongside the
   * signature, notification and unread maps: which layout you prefer is a preference about
   * the community, not about the machine you happen to be on.
   *
   * The in-memory profile is updated first so the toggle is instant — the write is a
   * round trip and the feed has already re-rendered by the time it lands.
   */
  setCommunityViewMode(stringId: string, mode: "list" | "detailed"): Promise<any> {
    // Written once for every community — `stringId` is kept so callers needn't change.
    return this.updateCommunityDefaults({ viewMode: mode });
  }

  /**
   * Fire-and-forget ping to `paeapi POST /communities/{stringId}/fan-out-new-post`
   * (DENG-733 Phase 3). Called right after `addPost` resolves for a parent post so
   * paeapi can build the follower recipient list and send `community_new_post`
   * notifications to everyone opted-in via `communityNotificationPreferences.new_posts`.
   *
   * Returns a Promise so callers can log errors, but this is intentionally fire-and-
   * forget — a failed ping shouldn't block the post-create flow or surface to the user
   * (the post is already in Firestore, notifications are a bonus). The server derives
   * the post author from the caller's authenticated session, not the body.
   */
  fanOutNewPost(communityStringId: string, payload: { postId: string; subject?: string; snippet?: string; authorName?: string; authorPhoto?: string }): Promise<any> {
    if (!communityStringId || !payload?.postId) return Promise.resolve();
    const url = `${environment.paeApiUrl}communities/${communityStringId}/fan-out-new-post`;
    return firstValueFrom(this.http.post(url, JSON.stringify(payload), this.helperService.getHttpOptions()));
  }

  getFirebaseProfile(id?) {
    // Resolve the target user id up front. handleFirestorePromise defers (and, on a
    // permission-denied, retries) the closure across awaits, so re-reading
    // this.userService.user.id inside the closure races with logout clearing the user and
    // throws "Cannot read properties of undefined (reading 'id')". Capturing the id here
    // keeps the lookup stable regardless of any later change to the signed-in user.
    const userId = id || this.userService?.user?.id;
    if (!userId) {
      return Promise.resolve(false);
    }
    return this.fbService
      .handleFirestorePromise(() => this.db.collection("user_profiles").doc(userId).ref.get())
      .then((result) => {
        if (result.exists) {
          let userData: any = result.data();
          this.repairProfileSearchFields(userId, userData);
          this.profile = this.buildCommunityUserData(userData);
          this.hasSetupCommunity = userData.hasSetupCommunity;
          this.firebaseProfile$.next(this.profile);
          return userData;
        } else {
          return false;
        }
      });
  }

  /**
   * Repairs this member's directory / @mention search keys on read.
   *
   * Nothing wrote `name_lowercase` when a profile was created, so most members
   * never had one, and a Firestore range query skips documents where the field
   * is missing — they were unfindable while rendering fine everywhere else.
   *
   * Doing it here rather than in a mass backfill means only accounts that
   * actually come back get written: at the time of writing, 1.9M of 2.45M
   * profiles needed the fix, and most of those will never post or be mentioned.
   * This read already happened, so the repair costs no extra read.
   *
   * Fire-and-forget: a member whose keys are a beat behind is no worse off than
   * one with none, and this must never delay or fail a profile load.
   */
  repairProfileSearchFields(userId: any, userData: any) {
    const user: any = this.userService?.user;
    // `refreshUserProfile(id)` can be called for someone else. Only ever repair
    // the signed-in member's own document — we only have THEIR name to go on.
    if (!user || !userId || `${userId}` !== `${user.id}`) return;

    // An explicit display name wins: a member may have renamed themselves to
    // something that is not first + last, and the key has to match what shows.
    const wanted = buildProfileSearchFields({ firstName: user.firstName, lastName: user.lastName, name: userData?.name });
    if (!wanted.name_lowercase) return;
    if (!profileSearchFieldsNeedUpdate(userData, wanted)) return;

    this.fbService
      .handleFirestorePromise(() =>
        this.db.collection("user_profiles").doc(`${userId}`).update({
          name_lowercase: wanted.name_lowercase,
          firstName_lowercase: wanted.firstName_lowercase,
          lastName_lowercase: wanted.lastName_lowercase,
        })
      )
      .catch((err) => console.warn("could not repair profile search keys", err));
  }

  addUserSkills(data, string_ids?) {
    // Use set with merge:true instead of update to handle new users who don't have a user_profiles document yet
    // Bug fix for: "No document to update: projects/mypresonus-abd2e/databases/(default)/documents/user_profiles/{userId}"
    this.fbService.handleFirestorePromise(() => this.db.collection("user_profiles").doc(this.userService.user.id).set({ skillsQueryMap: string_ids }, { merge: true }));
    return super.addUserSkills(data);
  }

  getUserProfile(userId?) {
    if (!userId) {
      return Promise.resolve(null);
    }
    return super.getUserProfile(userId, this.fbService.doc(`user_profiles/${userId}`).ref);
  }

  refreshUserProfile(userId?) {
    if (!userId) {
      return Promise.resolve(null);
    }
    return super.getUserProfile(userId, this.fbService.doc(`user_profiles/${userId}`).ref, true);
  }

  findMembersBySkills(skills, limit = 2000) {
    return super.findMembersBySkills(skills, limit, this.fbService.collection("user_profiles").ref);
  }

  findMembersBySkillsPaginated(skills: string[], limit: number = 50, lastDoc?: any): Promise<{ profiles: any[]; lastDoc: any; hasMore: boolean }> {
    let query = this.fbService.collection("user_profiles").ref.where("skillsQueryMap", "array-contains-any", skills).where("id", "!=", null).where("public", "==", true).limit(limit);
    if (lastDoc) {
      query = query.startAfter(lastDoc);
    }
    return query.get().then((result: any) => {
      let profiles = [];
      result.docs.forEach((item) => {
        let data = item.data();
        let skillsString = "";
        if (data.skillsQueryMap && data.skillsQueryMap.length) {
          // Post-DENG-728 channel migration: `skillsMap[string_id].title` is a per-locale
          // map (`{ en, es, ... }`) not a bare string. Concatenating renders "[object Object]".
          const lang = this.languageService.selectedLanguage?.code || "en";
          data.skillsQueryMap.forEach((string_id, index) => {
            const title = resolveLocalizedString(this.skillsMap[string_id]?.title, lang, string_id);
            skillsString += title + (index == data.skillsQueryMap.length - 1 ? "" : ", ");
          });
        }
        profiles.push({ ...data, skills_string: skillsString });
      });
      const lastVisible = result.docs.length ? result.docs[result.docs.length - 1] : null;
      return { profiles, lastDoc: lastVisible, hasMore: result.docs.length === limit };
    });
  }

  findMembersByName(skills) {
    return super.findMembersByName(skills, this.fbService.collection("user_profiles"));
  }

  findMembers(skills?, nameQuery?, emailQuery?, limit = 5, experts?) {
    return super.findMembers(this.fbService.collection("user_profiles").ref, skills, nameQuery, emailQuery, limit, experts);
  }

  findModeratedUsers() {
    return super.findModeratedUsers(this.fbService.collection("user_profiles").ref);
  }

  addPost(data, collection: string = environment.communityFirestoreCollection) {
    // Stamp every new public-community post with its community_string_id so post-multi-community
    // Firestore/Algolia queries can filter by it. Beta writes to `beta_community_posts` and
    // already tags by `beta_string_id`, so we leave those alone.
    if (collection === environment.communityFirestoreCollection && !data.community_string_id) {
      data = { ...data, community_string_id: FSP_COMMUNITY_STRING_ID };
    }
    // Through handleFirestorePromise like every read: a write rejected with permission-denied
    // because the Firebase custom-token session lapsed (long compose, Safari suspending the tab)
    // re-signs in and retries once, instead of failing. It used to be a bare `.add()`, so the
    // post was lost while the composer showed it as sent.
    return this.fbService.handleFirestorePromise<any>(() => super.addPost(data, this.fbService.collection(collection)));
  }

  getThreadMessages(type, thread?, limit = 100, page = 1) {
    return new Promise((resolve, reject) => {
      let messages = [];
      let method;
      if (type == "connection_thread" && thread) {
        method = this.fbService.collection(`connection_threads/${thread.id}/messages`).ref.orderBy("timestamp").limitToLast(100);
      }
      this.fbService.handleFirestorePromise(() => {
        return method.get().then((result) => {
          result.docs.forEach((doc) => {
            let msg: any = doc.data();
            msg.key = doc.id;
            msg.id = doc.id;
            if (msg.file) msg.file.url = this.buildFileUrl(msg.file.user_id, msg.file);
            if (msg.message) this.buildHtmlComment(msg);
            messages.push(msg);
          });
          this.activeThreadMessages = messages;
          resolve(messages);
        });
      });
    });
  }

  public justRecievedAddedMessage: any;
  watchThreadMessages(type, thread?) {
    // Unsubscribe from any existing subscription
    if (this.activeThreadMessagesSubscription) {
      this.activeThreadMessagesSubscription.unsubscribe();
    }

    this.activeThreadMessagesSubscription = this.fbService
      .handleFirestoreObservable(() =>
        this.db
          .collection("connection_threads")
          .doc(thread.id)
          .collection("messages", (ref) => ref.orderBy("timestamp", "desc"))
          .stateChanges()
          .pipe(
            map((data) => {
              data.forEach((itemDoc) => {
                let item: any = itemDoc.payload.doc.data();
                item.key = itemDoc.payload.doc.id;
                item.id = itemDoc.payload.doc.id;

                // Helper function to update a message
                const updateMessage = (msg) => {
                  msg.message = item.message;
                  if (msg.message) this.buildHtmlComment(msg);
                  if (item.edited) msg.edited = true;
                  return msg;
                };

                // Handle "added" messages
                if (itemDoc.type === "added") {
                  if (!item.timestamp) {
                    // Skip items without a `timestamp`
                    return;
                  }

                  // Add to activeThreadMessages if not already present
                  if (!this.activeThreadMessages.some((msg) => msg.id === item.id)) {
                    this.activeThreadMessages.push(updateMessage(item));
                    this.threadMessageEvent$.next("added");
                  }
                } else if (itemDoc.type === "removed") {
                  // Handle "removed" messages
                  const removeIndex = this.activeThreadMessages.findIndex((msg) => msg.id === item.id);
                  if (removeIndex !== -1) {
                    this.activeThreadMessages.splice(removeIndex, 1);
                    this.threadMessageEvent$.next("removed");
                  }
                } else if (itemDoc.type === "modified") {
                  // Handle "modified" messages
                  const existingMessage = this.activeThreadMessages.find((msg) => msg.id === item.id);

                  if (existingMessage) {
                    // Update existing message
                    updateMessage(existingMessage);
                    this.threadMessageEvent$.next("modified");
                  } else if (item.timestamp) {
                    // Add the message if it's not already present and has a valid timestamp
                    this.activeThreadMessages.push(updateMessage(item));
                    this.threadMessageEvent$.next("added");
                  }
                }
              });
              this.activeThreadMessages.sort((a, b) => {
                return a.timestamp.toDate() > b.timestamp.toDate() ? 1 : -1;
              });
              return this.activeThreadMessages;
            })
          )
      )
      .subscribe();
  }

  getPostByID(id, ignoreReplies = false, rejectWhenNotFound = true, collection: string = environment.communityFirestoreCollection, includeDeleted = false) {
    return new Promise((resolve, reject) => {
      let posts = [];
      this.fbService
        .handleFirestorePromise(() => this.db.collection(collection).doc(id).ref.get())
        .then((result) => {
          let post: any = result.data();
          if (post && (!post.deleted || includeDeleted)) {
            post.key = result.id;
            post.id = result.id;
            if (collection === "beta_community_posts" && !post.type) {
              post.type = post.parentID ? "reply" : "post";
              this.fbService.doc(`${collection}/${result.id}`).update({ type: post.type });
            }
            if (ignoreReplies) {
              resolve(this.buildHtmlComment(post));
            } else {
              let promises = [];
              posts.push(post);
              promises.push(
                this.fbService
                  .collection(collection)
                  .ref.where("parentID", "==", result.id)
                  .get()
                  .then((replies) => {
                    replies.docs.forEach((replyDoc) => {
                      let reply: any = replyDoc.data();
                      if (!reply.deleted || includeDeleted) {
                        reply.key = replyDoc.id;
                        reply.id = replyDoc.id;
                        if (collection === "beta_community_posts" && !reply.type) {
                          reply.type = "reply";
                          this.fbService.doc(`${collection}/${replyDoc.id}`).update({ type: reply.type });
                        }
                        posts.push(reply);
                      }
                    });
                  })
              );
              posts.forEach((item) => {
                this.buildHtmlComment(item);
              });
              return Promise.all(promises).then((result) => {
                // this.processPostsAndReplies(posts);
                resolve(posts);
              });
            }
          } else {
            if (rejectWhenNotFound) {
              reject(new Error("Post not found."));
            } else {
              resolve(null);
            }
          }
        });
    });
  }

  watchPostReplies(id, collection: string = environment.communityFirestoreCollection) {
    return this.fbService.handleFirestoreObservable(() =>
      this.db
        .collection(collection, (ref) => {
          return ref.where("parentID", "==", id).orderBy("created", "asc");
        })
        .snapshotChanges()
        .pipe(
          skip(1),
          map((result) => {
            let posts = [];
            result.forEach((itemDoc) => {
              let item: any = itemDoc.payload.doc.data();
              if (item.employee_only && !this.userService.isEmployee) {
                // dont add because its employee only
              } else if (!item.deleted) {
                item.key = itemDoc.payload.doc.id;
                item.id = itemDoc.payload.doc.id;
                if (collection === "beta_community_posts" && !item.type) {
                  item.type = "reply";
                  this.fbService.doc(`${collection}/${itemDoc.payload.doc.id}`).update({ type: item.type });
                }
                this.buildHtmlComment(item);
                posts.push(item);
              }
            });
            return posts;
          })
        )
    );
  }

  unwatchPostReplies() {
    this.activePostDetailSubscription.unsubscribe();
  }

  getBetaCommunityDetails(betaStringId) {
    // gets tag counts and author list
    if (betaStringId !== this._betaAuthorsFor) {
      // Different beta: drop the previous beta's authors. The last list seen for this beta (if any)
      // shows right away; otherwise the map is "not loaded" until the Firestore query below lands.
      this._betaAuthorsFor = betaStringId;
      this.setBetaAuthors(this.readCachedBetaAuthors(betaStringId));
      this.filteredBetaAuthorList = null;
    }
    // ponytail: scans every parent post of the beta just to collect authors (~25s on large betas);
    // a per-beta authors endpoint/doc would make the first visit fast too.
    this.betaDetailsPromise = this.fbService
      .handleFirestorePromise(() => this.db.collection("beta_community_posts").ref.where("beta_string_id", "==", betaStringId).where("parentID", "==", null).get())
      .then(
        (result) => {
          // A newer call switched betas while this query was in flight; its own response populates the map.
          if (betaStringId !== this._betaAuthorsFor) return;
          // Tag counts are now managed by Algolia facet queries in the channel component
          const map = {};
          result.docs.forEach((doc) => {
            const data: any = doc.data();
            if (!data.deleted) map[data.author.id] = data.author;
          });
          this.setBetaAuthors(Object.values(map));
          this.writeCachedBetaAuthors(betaStringId, this.betaAuthorList);
        },
        () => {
          // Query failed: end the dropdown's loading state (keep a cached list if one is showing).
          if (betaStringId === this._betaAuthorsFor && !this.betaAuthorMap) this.setBetaAuthors([]);
        }
      );
    return this.betaDetailsPromise;
  }

  private setBetaAuthors(authors: any[] | null) {
    if (!authors) {
      this.betaAuthorList = null;
      this.betaAuthorMap = null;
      return;
    }
    this.betaAuthorMap = {};
    authors.forEach((a) => (this.betaAuthorMap[a.id] = a));
    this.betaAuthorList = Object.values(this.betaAuthorMap).sort((a: any, b: any) => (a.name > b.name ? 1 : -1));
  }

  private readCachedBetaAuthors(betaStringId: string): any[] | null {
    try {
      const list = JSON.parse(localStorage.getItem(BETA_AUTHORS_CACHE_PREFIX + betaStringId));
      return Array.isArray(list) ? list : null;
    } catch {
      return null;
    }
  }

  private writeCachedBetaAuthors(betaStringId: string, authors: any[]) {
    try {
      localStorage.setItem(BETA_AUTHORS_CACHE_PREFIX + betaStringId, JSON.stringify(authors.map((a) => ({ id: a.id, name: a.name }))));
    } catch {
      // Storage full or blocked: the cache is only a speed-up.
    }
  }

  getPostsWithReplies(args: {
    channel?: any;
    topic?: any;
    postsAccountID?: string;
    moderationAccountID?: string;
    pins?: boolean;
    mostPopular?: boolean;
    limit?: number;
    clear?: boolean;
    collection?: string;
    betaStringId?: string;
    communityStringId?: string;
    /** Leave out the community's Featured rail posts — see the core `getPostsWithReplies`. */
    excludeFeatured?: boolean;
  }) {
    if (!args.collection) args.collection = environment.communityFirestoreCollection;
    return super.getPostsWithReplies(args, this.fbService.collection(args.collection).ref);
  }

  /**
   * Fetch the single latest parent post for a community — used by the `/communities`
   * landing to show a "latest post" snippet on each community card.
   *
   * Filters to `type == "post"` SERVER-side. It used to take the newest 20 docs and skip
   * replies in the loop, which silently failed on any community whose recent traffic is
   * replies rather than new threads: Tone Master Pro had 23 consecutive replies at the head
   * of the collection, so all 20 docs were replies and the card showed no post at all while
   * the avatar strip (which reads 24 docs and keeps repliers) filled in normally.
   *
   * `type` rather than `parentID`: parent posts don't carry a `parentID` field at all, so
   * `where("parentID", "==", null)` matches nothing.
   *
   * Still reads a few docs rather than one so a deleted newest post doesn't blank the card.
   *
   * Returns null when the community has no parent posts or on error — the card is
   * expected to render gracefully without a snippet.
   */
  getLatestPostForCommunity(stringId: string, collection: string = environment.communityFirestoreCollection): Promise<any | null> {
    return this.fbService
      .handleFirestorePromise(() => this.db.collection(collection).ref.where("community_string_id", "==", stringId).where("type", "==", "post").orderBy("created", "desc").limit(5).get())
      .then((snap: any) => {
        if (!snap || snap.empty) return null;
        for (const doc of snap.docs) {
          const data = doc.data();
          if (data.deleted) continue;
          data.key = doc.id;
          data.id = doc.id;
          this.buildHtmlComment(data);
          return data;
        }
        return null;
      })
      .catch((err: any) => {
        // Logged, not swallowed: this query needs a composite index, and Firestore returns
        // the console link to create it in the error message.
        console.error("getLatestPostForCommunity failed", err?.message || err);
        return null;
      });
  }

  /**
   * Combined recent-posts feed across a set of communities — parents only, sorted by
   * `created` desc. Backs the right-rail "latest across your communities" widget on
   * the `/communities` landing. Firestore's `in` operator supports up to 30 values, so
   * this is comfortable for the current ~3-5 community range.
   *
   * Filters to `type == "post"` server-side for the same reason `getLatestPostForCommunity`
   * does — a reply-heavy community would otherwise crowd every other community's threads out
   * of the fetched window. Still over-fetches a little so the deleted skip below has slack.
   * Returns `[]` on empty or error.
   */
  getRecentPostsForCommunities(stringIds: string[], limit: number = 20, collection: string = environment.communityFirestoreCollection): Promise<any[]> {
    if (!stringIds?.length) return Promise.resolve([]);
    return this.fbService
      .handleFirestorePromise(() =>
        this.db
          .collection(collection)
          .ref.where("community_string_id", "in", stringIds)
          .where("type", "==", "post")
          .orderBy("created", "desc")
          .limit(limit * 2)
          .get()
      )
      .then((snap: any) => {
        if (!snap || snap.empty) return [];
        const parents: any[] = [];
        for (const d of snap.docs) {
          const data = d.data();
          if (data.deleted) continue;
          data.key = d.id;
          data.id = d.id;
          this.buildHtmlComment(data);
          parents.push(data);
          if (parents.length >= limit) break;
        }
        return parents;
      })
      .catch((err: any) => {
        console.error("getRecentPostsForCommunities failed", err?.message || err);
        return [];
      });
  }

  /**
   * Return up to `count` unique authors from a community's recent posts (parents +
   * replies). Used for the avatar strip on the community card at `/communities`.
   * Over-fetches (count × 4) to increase the odds of finding `count` distinct authors
   * without a second query.
   */
  getRecentAuthorsForCommunity(
    stringId: string,
    count: number = 6,
    collection: string = environment.communityFirestoreCollection
  ): Promise<Array<{ id: string; name: string; photo?: string | null }>> {
    return this.fbService
      .handleFirestorePromise(() =>
        this.db
          .collection(collection)
          .ref.where("community_string_id", "==", stringId)
          .orderBy("created", "desc")
          .limit(count * 4)
          .get()
      )
      .then((snap: any) => {
        if (!snap || snap.empty) return [];
        const seen = new Set<string>();
        const authors: any[] = [];
        for (const d of snap.docs) {
          const data = d.data();
          const author = data?.author;
          if (!author?.id || seen.has(author.id)) continue;
          seen.add(author.id);
          authors.push(author);
          if (authors.length >= count) break;
        }
        return authors;
      })
      .catch(() => []);
  }

  public watchingCollectionRef: any;
  watchPostsWithReplies(
    channel?,
    topic?,
    postsAccountID?,
    pins?,
    collection: string = environment.communityFirestoreCollection,
    betaStringId?: string,
    watchPostsFrom?: Date,
    communityStringId?: string
  ) {
    if (this.activePostsSubscription) this.activePostsSubscription.unsubscribe();
    if (this.watchingCollectionRef) this.watchingCollectionRef.unsubscribe();
    this.activePostsSubscription = undefined;

    // Get channel ID for filtering (capture it here so it's available in the map function)
    const channelIdString = channel?.string_id || channel?.id;

    // When filters change, clear existing posts that don't match the new filters
    if (betaStringId && this.activeParentPosts && this.activeParentPosts.length > 0) {
      this.activeParentPosts = this.activeParentPosts.filter((post) => {
        const shouldDisplay = this.betaCommunityService.shouldDisplayPost(post, channelIdString, this.userService.user?.id);
        if (!shouldDisplay) {
          // Also remove from displayedPostsMap
          delete this.displayedPostsMap[post.key];
        }
        return shouldDisplay;
      });
    }

    // When this watcher began. `added` events for documents OLDER than this are the initial
    // snapshot, not new activity — see the guard in the handler below.
    const watchStartedAt = Date.now();

    this.activePostsSubscription = this.fbService
      .handleFirestoreObservable(() => {
        return this.db
          .collection(collection, (ref) => {
            if (betaStringId) {
              if (channel && channel.string_id != "all_posts" && channel.string_id != "pinned_posts" && channel.string_id != "my_posts") {
                return ref.where("channels", "array-contains-any", [channel.string_id]).where("beta_string_id", "==", betaStringId).orderBy("created", "asc");
              } else {
                if (watchPostsFrom) {
                  return ref.where("created", ">=", watchPostsFrom).where("beta_string_id", "==", betaStringId).orderBy("created", "asc");
                } else {
                  return ref.where("beta_string_id", "==", betaStringId).orderBy("created", "asc");
                }
              }
            } else {
              // Non-beta path: optionally scope to a specific community.
              // Each combination below will require a Firestore composite index the first
              // time it runs against a community filter — the console error message
              // includes a "create index" link that will build it in one click.
              if (channel && channel.string_id != "all_posts" && channel.string_id != "pinned_posts" && channel.string_id != "my_posts") {
                let q: any = ref.where("channels", "array-contains-any", [channel.string_id]);
                if (communityStringId) q = q.where("community_string_id", "==", communityStringId);
                return q.orderBy("created", "asc");
              } else if (topic) {
                let q: any = ref.where("topics", "array-contains-any", [topic.string_id]);
                if (communityStringId) q = q.where("community_string_id", "==", communityStringId);
                return q.orderBy("created", "asc");
              } else {
                let threeDaysAgo = new Date();
                threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
                let q: any = ref.where("created", ">=", threeDaysAgo);
                if (communityStringId) q = q.where("community_string_id", "==", communityStringId);
                return q.orderBy("created", "asc");
              }
            }
          })
          .stateChanges()
          .pipe(
            skip(1),
            map((result) => {
              if (result.length) {
                let posts = [];
                // Subscribing to `stateChanges()` replays every matching document as `added`,
                // and that initial snapshot has to be told apart from real activity. It used
                // to be guessed from batch size — "more than 5 at once is probably the first
                // get" — which silently dropped genuine events whenever several people posted
                // or replied at the same moment, exactly when the feed most needs to keep up.
                //
                // Judged by age instead: an `added` document older than the moment this
                // watcher started is the replay, a newer one is real. Deterministic, and
                // independent of how many arrive together. `modified` and `removed` are always
                // real changes and are processed regardless.
                {
                  result.forEach((itemDoc) => {
                    let item: any = itemDoc.payload.doc.data();
                    this.pendingPosts = this.pendingPosts.filter((post) => post.subject != item.subject);
                    item.key = itemDoc.payload.doc.id;
                    item.id = itemDoc.payload.doc.id;
                    if ((itemDoc.type == "modified" && item.deleted) || itemDoc.type == "removed") {
                      if (item.parentID) {
                        let removeIndex;
                        if (this.activeParentThreadMap[item.parentID] && this.activeParentThreadMap[item.parentID].length) {
                          this.activeParentThreadMap[item.parentID].forEach((msg, i) => {
                            if (msg.id == item.id) removeIndex = i;
                          });
                          if (removeIndex || removeIndex === 0) this.activeParentThreadMap[item.parentID].splice(removeIndex, 1);
                        }
                      } else {
                        let removeIndex;
                        this.activeParentPosts.forEach((msg, i) => {
                          if (msg.id == item.id) removeIndex = i;
                        });
                        if (removeIndex || removeIndex === 0) this.activeParentPosts.splice(removeIndex, 1);
                      }
                    } else if (itemDoc.type == "added") {
                      const createdAt = item.created?.toDate ? item.created.toDate().getTime() : Number(item.created) || 0;
                      if (createdAt && createdAt < watchStartedAt) {
                        // Part of the initial replay — the feed's own load is responsible for
                        // these, and re-adding them here would inject documents the feed
                        // deliberately did not show (the watch window is wider than a page).
                      } else if (pins && !item.parentID) {
                        // we are watching pins, and theres no way a new post could already be pinned, so ignore.
                      } else {
                        if (!postsAccountID || item.author.id == postsAccountID || item.parentID) {
                          if (item.employee_only && !this.userService.isEmployee) {
                            // dont add because its employee only
                          } else if (item.pinned) {
                            // dont add because its a pinned post in a beta program
                          } else if (!betaStringId && !postsAccountID && !item.parentID && environment.features?.communityFeaturedRail && isFeaturedNow(item)) {
                            // dont add because it went straight into the Featured rail (DENG-1294)
                          } else if (!topic && item.topics && item.topics.length) {
                            // dont add if we arent watching a topic, and this post has topics
                          } else if (betaStringId && !this.betaCommunityService.shouldDisplayPost(item, channelIdString, this.userService.user?.id)) {
                            // dont add because it doesn't match channel or beta filters
                          } else {
                            this.buildHtmlComment(item);
                            if (!item.parentID) {
                              if (!this.activeParentThreadMap[item.key]) this.activeParentThreadMap[item.key] = [];
                              this.displayedPostsMap[item.key] = item;
                              // check if the post is already in activeParentPosts before adding it.
                              let alreadyAdded = false;
                              this.activeParentPosts.forEach((msg) => {
                                if (msg.id == item.id) alreadyAdded = true;
                              });
                              if (!alreadyAdded) {
                                this.activeParentPosts.push(item);
                                this.sortActiveParentPosts();
                              }
                            } else {
                              if (!this.activeParentThreadMap[item.parentID]) this.activeParentThreadMap[item.parentID] = [];
                              this.displayedPostsMap[item.key] = item;
                              // check if the post is already in the thread map array before adding it
                              let alreadyAdded = false;
                              this.activeParentThreadMap[item.parentID].forEach((msg) => {
                                if (msg.id == item.id) alreadyAdded = true;
                              });
                              if (!alreadyAdded) this.activeParentThreadMap[item.parentID].push(item);
                            }
                          }
                        }
                      }
                    } else if (itemDoc.type == "modified") {
                      if (!itemDoc.payload.doc.metadata.hasPendingWrites) {
                        const update = (msg) => {
                          if (item.message !== msg.message) {
                            msg.message = item.message;
                            msg.htmlMessage = item.htmlMessage;
                            this.buildHtmlComment(msg);
                            delete msg._searchQuery;
                            delete msg.highlightSnippet;
                            delete msg._rawSubject;
                          }
                          if (msg.file) msg.file.url = this.buildFileUrl(msg.file.user_id, msg.file);
                          if (item.subject) {
                            const rawSubject = msg._rawSubject || msg.subject;
                            if (item.subject !== rawSubject) {
                              msg.subject = item.subject;
                              delete msg._rawSubject;
                            }
                          }
                          msg.beta_type = item.beta_type || "none";
                          msg.beta_status = item.beta_status || "none";
                          msg.beta_review_details = item.beta_review_details;
                          msg.reviewed = item.reviewed;
                          this.applyReactionFieldsFromDoc(msg, item);
                          if (item.pins) msg.pins = item.pins;
                          if (item.flags) msg.flags = item.flags;
                          if (item.cleared || item.cleared === false) msg.cleared = item.cleared;
                          if (item.edited) msg.edited = true;
                          if (item.articles) msg.articles = item.articles;
                          if (item.learn_content) msg.learn_content = item.learn_content;
                          if (item.categories_or_sections) msg.categories_or_sections = item.categories_or_sections;
                          if (item.pinned !== undefined) msg.pinned = item.pinned;
                          if (item.allow_comments !== undefined) msg.allow_comments = item.allow_comments;
                        };
                        if (item.parentID) {
                          if (this.activeParentThreadMap[item.parentID] && this.activeParentThreadMap[item.parentID].length) {
                            this.activeParentThreadMap[item.parentID].forEach((msg) => {
                              if (msg.id == item.id) msg = update(msg);
                            });
                          }
                        } else {
                          this.activeParentPosts.forEach((msg) => {
                            if (msg.id == item.id) {
                              update(msg);
                            }
                          });
                          this.sortActiveParentPosts();
                        }
                      }
                    }
                    for (var x in this.activeParentThreadMap) {
                      this.activeParentThreadMap[x].sort((a, b) => {
                        return a.created > b.created ? 1 : -1;
                      });
                    }
                  });
                }
                return posts;
              }
            })
          );
      })
      .subscribe();
  }

  /**
   * Merge reaction-related fields from a Firestore doc onto an in-memory post.
   * Feed posts often arrive from Algolia first (stale/missing reactions); Firestore is source of truth.
   */
  private applyReactionFieldsFromDoc(msg: any, item: any) {
    msg.likes = item.likes || [];
    msg.reactions = item.reactions || {};
    if (item.likeCount !== undefined) {
      msg.likeCount = item.likeCount;
    } else {
      msg.likeCount = Object.keys(normalizeReactions(item)).length;
    }
  }

  /**
   * Watch individual displayed posts by their IDs.
   * This is more efficient than watching an entire collection query for large datasets.
   * Use this for beta communities with many posts to avoid performance issues.
   *
   * @param postIds Array of post IDs to watch
   * @param collection Firestore collection name
   * @param appendMode If true, adds watchers for new posts without clearing existing ones (for "load more")
   */
  watchDisplayedPosts(postIds: string[], collection: string = environment.communityFirestoreCollection, appendMode: boolean = false) {
    // If not in append mode, clear existing watchers first
    if (!appendMode) {
      this.unwatchDisplayedPosts();
    }

    // Get IDs we're already watching to avoid duplicates in append mode
    const watchedIds = new Set(this.displayedPostWatchers.map((w) => w.postId));

    postIds.forEach((postId) => {
      // Skip if already watching this post
      if (watchedIds.has(postId)) return;

      const subscription = this.fbService
        .handleFirestoreObservable(() =>
          this.db
            .collection(collection)
            .doc(postId)
            .snapshotChanges()
            .pipe(
              // Do not skip the first snapshot: Algolia-loaded posts may lack/lag `reactions`.
              // Merge carefully so search highlights on message/subject are preserved.
              map((snapshot) => {
                if (!snapshot.payload.exists) {
                  // Document was deleted
                  return { type: "removed", id: postId, data: null };
                }
                const data: any = snapshot.payload.data();
                data.key = snapshot.payload.id;
                data.id = snapshot.payload.id;
                return { type: "modified", id: postId, data };
              })
            )
        )
        .subscribe((result: any) => {
          if (result.type === "removed" || (result.data && result.data.deleted)) {
            // Handle removed/deleted post
            if (result.data?.parentID) {
              const removeIndex = this.activeParentThreadMap[result.data.parentID]?.findIndex((msg) => msg.id === result.id);
              if (removeIndex !== undefined && removeIndex !== -1) {
                this.activeParentThreadMap[result.data.parentID].splice(removeIndex, 1);
              }
            } else {
              const removeIndex = this.activeParentPosts?.findIndex((msg) => msg.id === result.id);
              if (removeIndex !== undefined && removeIndex !== -1) {
                this.activeParentPosts.splice(removeIndex, 1);
              }
            }
          } else if (result.type === "modified" && result.data) {
            const item = result.data;
            this.buildHtmlComment(item);

            const update = (msg) => {
              // Only rebuild message/htmlMessage if content actually changed,
              // to preserve Algolia search highlights on the displayed posts
              if (item.message !== msg.message) {
                msg.message = item.message;
                msg.htmlMessage = item.htmlMessage;
                this.buildHtmlComment(msg);
                // Content changed — clear stale search highlights
                delete msg._searchQuery;
                delete msg.highlightSnippet;
                delete msg._rawSubject;
              }
              if (msg.file) msg.file.url = this.buildFileUrl(msg.file.user_id, msg.file);
              if (item.subject) {
                // Compare against raw (unhighlighted) subject to avoid stripping Algolia highlights
                const rawSubject = msg._rawSubject || msg.subject;
                if (item.subject !== rawSubject) {
                  msg.subject = item.subject;
                  delete msg._rawSubject;
                }
              }
              msg.beta_type = item.beta_type || "none";
              msg.beta_status = item.beta_status || "none";
              msg.beta_review_details = item.beta_review_details;
              msg.reviewed = item.reviewed;
              this.applyReactionFieldsFromDoc(msg, item);
              if (item.pins) msg.pins = item.pins;
              if (item.flags) msg.flags = item.flags;
              if (item.cleared || item.cleared === false) msg.cleared = item.cleared;
              if (item.edited) msg.edited = true;
              if (item.articles) msg.articles = item.articles;
              if (item.learn_content) msg.learn_content = item.learn_content;
              if (item.categories_or_sections) msg.categories_or_sections = item.categories_or_sections;
              if (item.pinned !== undefined) msg.pinned = item.pinned;
              if (item.allow_comments !== undefined) msg.allow_comments = item.allow_comments;
              // Thread summary, kept by the reply-stats function. In the list layout this is
              // how a row's count and "latest activity" move when someone replies, since the
              // replies themselves aren't loaded. The sort below re-derives `replyCount`.
              if (item.reply_count !== undefined) msg.reply_count = item.reply_count;
              if (item.last_reply_at !== undefined) msg.last_reply_at = item.last_reply_at;
              if (item.last_reply_author_id !== undefined) msg.last_reply_author_id = item.last_reply_author_id;
              if (item.last_activity_at !== undefined) msg.last_activity_at = item.last_activity_at;
            };

            if (item.parentID) {
              // This is a reply
              if (this.activeParentThreadMap[item.parentID]?.length) {
                this.activeParentThreadMap[item.parentID].forEach((msg) => {
                  if (msg.id === item.id) update(msg);
                });
              }
            } else {
              // This is a parent post
              this.activeParentPosts?.forEach((msg) => {
                if (msg.id === item.id) update(msg);
              });
              this.sortActiveParentPosts();
            }
          }
        });

      this.displayedPostWatchers.push({ postId, subscription });
    });
  }

  /**
   * Unsubscribe from all individual post watchers
   */
  unwatchDisplayedPosts() {
    this.displayedPostWatchers.forEach((watcher) => {
      if (watcher.subscription) {
        watcher.subscription.unsubscribe();
      }
    });
    this.displayedPostWatchers = [];
  }

  public newPostsSubscription: any;
  /**
   * Watch for new posts created after a given timestamp.
   * This is used alongside watchDisplayedPosts to catch new posts/replies.
   * The query initially matches 0 documents and only picks up newly created posts.
   *
   * @param watchFrom Timestamp to watch from (typically "now" when page loads)
   * @param collection Firestore collection name
   * @param betaStringId Beta program string ID to filter by
   * @param channel Optional channel to filter by
   */
  watchNewPosts(watchFrom: Date, collection: string = environment.communityFirestoreCollection, betaStringId?: string, channel?: any, communityStringId?: string) {
    if (this.newPostsSubscription) this.newPostsSubscription.unsubscribe();

    this.newPostsSubscription = this.fbService
      .handleFirestoreObservable(() => {
        return this.db
          .collection(collection, (ref) => {
            if (betaStringId) {
              if (channel && channel.string_id != "all_posts" && channel.string_id != "pinned_posts" && channel.string_id != "my_posts") {
                return ref.where("channels", "array-contains-any", [channel.string_id]).where("beta_string_id", "==", betaStringId).where("created", ">", watchFrom).orderBy("created", "asc");
              } else {
                return ref.where("beta_string_id", "==", betaStringId).where("created", ">", watchFrom).orderBy("created", "asc");
              }
            } else {
              let q: any = ref.where("created", ">", watchFrom);
              if (communityStringId) q = q.where("community_string_id", "==", communityStringId);
              return q.orderBy("created", "asc");
            }
          })
          .stateChanges()
          .pipe(
            map((result) => {
              result.forEach((itemDoc) => {
                let item: any = itemDoc.payload.doc.data();
                item.key = itemDoc.payload.doc.id;
                item.id = itemDoc.payload.doc.id;

                if (itemDoc.type === "added") {
                  // Remove from pending posts (the queued post with spinner)
                  this.pendingPosts = this.pendingPosts.filter((post) => post.subject != item.subject);

                  if (item.employee_only && !this.userService.isEmployee) {
                    // dont add because its employee only
                  } else {
                    this.buildHtmlComment(item);
                    if (!item.parentID) {
                      // New parent post
                      if (!this.activeParentThreadMap[item.key]) this.activeParentThreadMap[item.key] = [];
                      this.displayedPostsMap[item.key] = item;
                      // Check if already added
                      let alreadyAdded = this.activeParentPosts.some((msg) => msg.id === item.id);
                      if (!alreadyAdded) {
                        this.activeParentPosts.push(item);
                        this.sortActiveParentPosts();
                        // Also start watching this new post for modifications
                        this.watchDisplayedPosts([item.id], collection, true);
                      }
                    } else {
                      // New reply
                      if (!this.activeParentThreadMap[item.parentID]) this.activeParentThreadMap[item.parentID] = [];
                      this.displayedPostsMap[item.key] = item;
                      let alreadyAdded = this.activeParentThreadMap[item.parentID].some((msg) => msg.id === item.id);
                      if (!alreadyAdded) {
                        this.activeParentThreadMap[item.parentID].push(item);
                        this.activeParentThreadMap[item.parentID].sort((a, b) => {
                          return a.created > b.created ? 1 : -1;
                        });
                        this.sortActiveParentPosts();
                        // Also start watching this new reply for modifications
                        this.watchDisplayedPosts([item.id], collection, true);
                      }
                    }
                  }
                }
              });
              return result;
            })
          );
      })
      .subscribe();
  }

  /**
   * Unsubscribe from new posts watcher
   */
  unwatchNewPosts() {
    if (this.newPostsSubscription) {
      this.newPostsSubscription.unsubscribe();
      this.newPostsSubscription = undefined;
    }
  }

  private getCommunityListCacheKey(betaStringId: string | undefined, channel: any): string {
    const channelId = channel?.string_id || channel?.id || "all_posts";
    return `${betaStringId || "community"}:${channelId}`;
  }

  saveCommunityListBackCache(betaStringId: string | undefined, channel: any): void {
    if (!this.activeParentPosts?.length) return;
    const key = this.getCommunityListCacheKey(betaStringId, channel);
    this.communityListBackCache = {
      key,
      activeParentPosts: [...this.activeParentPosts],
      activeParentThreadMap: { ...this.activeParentThreadMap },
      lastVisiblePost: this.lastVisiblePost,
      lastVisibleRecentReply: this.lastVisibleRecentReply,
      activePostsLimitReached: this.activePostsLimitReached,
    };
  }

  enrichCommunityListBackCacheForPostNavigation(
    viewedPostId: string,
    algoliaState?: {
      algoliaPage: number;
      algoliaTotalPages: number;
      currentAlgoliaFilters: string;
      currentAlgoliaQuery: string;
      /** Page 1 came from the recent-activity replica, so later pages must too. */
      usingActivityIndex?: boolean;
    }
  ): void {
    if (!this.communityListBackCache) return;
    this.communityListBackCache.viewedPostId = viewedPostId;
    this.communityListBackCache.returnUrl = this.currentCommunityUrl;
    this.communityListBackCache.betaTagCountMap = this.betaTagCountMap;
    this.communityListBackCache.filteredBetaAuthorList = this.filteredBetaAuthorList;
    if (algoliaState) {
      this.communityListBackCache.algoliaState = algoliaState;
    }
  }

  restoreFromCommunityListBackCache(betaStringId: string | undefined, channel: any): { algoliaState?: any } | null {
    const key = this.getCommunityListCacheKey(betaStringId, channel);
    if (!this.communityListBackCache || this.communityListBackCache.key !== key) return null;
    this.activeParentPosts = [...this.communityListBackCache.activeParentPosts];
    this.activeParentThreadMap = {};
    for (const k in this.communityListBackCache.activeParentThreadMap) {
      this.activeParentThreadMap[k] = [...(this.communityListBackCache.activeParentThreadMap[k] || [])];
    }
    this.lastVisiblePost = this.communityListBackCache.lastVisiblePost;
    this.lastVisibleRecentReply = this.communityListBackCache.lastVisibleRecentReply;
    this.activePostsLimitReached = this.communityListBackCache.activePostsLimitReached;
    if (this.communityListBackCache.betaTagCountMap) this.betaTagCountMap = this.communityListBackCache.betaTagCountMap;
    if (this.communityListBackCache.filteredBetaAuthorList) this.filteredBetaAuthorList = this.communityListBackCache.filteredBetaAuthorList;
    this.displayedPostsMap = {};
    this.activeParentPosts.forEach((p) => (this.displayedPostsMap[p.key] = p));
    for (const parentId in this.activeParentThreadMap) {
      this.activeParentThreadMap[parentId].forEach((r) => (this.displayedPostsMap[r.key] = r));
    }
    const algoliaState = this.communityListBackCache.algoliaState;
    this.communityListBackCache = null;
    return { algoliaState };
  }

  clearCommunityListBackCache(): void {
    this.communityListBackCache = null;
  }

  unwatch(): void {
    this.displayedPostsMap = {};
    this.activeParentPosts = [];
    this.activeParentThreadMap = {};

    this.activeThreadMessages = [];
    if (this.activeThreadMessagesSubscription) this.activeThreadMessagesSubscription.unsubscribe();
    if (this.activePostsSubscription) {
      this.activePostsSubscription.unsubscribe();
      this.activePostsSubscription = undefined;
    }
    if (this.watchingCollectionRef) {
      this.watchingCollectionRef.unsubscribe();
      this.watchingCollectionRef = undefined;
    }
    this.unwatchDisplayedPosts();
    this.unwatchNewPosts();
  }

  public comments: Array<any> = [];
  public replies: Array<any> = [];
  public parentThreadMap: any = {};
  public processedCommentMap: any = {};
  public commentSubscription: any;

  public subscribeToComments(ref, sortOrder?) {
    return ref.stateChanges().pipe(
      map((data: any) => {
        data.forEach((itemDoc) => {
          let item: any = itemDoc.payload.doc.data();
          item.key = itemDoc.payload.doc.id;
          item.id = itemDoc.payload.doc.id;

          // Ensure parent thread map is initialized
          if (!item.parentID && !this.parentThreadMap[item.id]) this.parentThreadMap[item.id] = [];

          // Build HTML for the comment
          item = this.buildHtmlComment(item);

          // Handle "added" comments
          if (itemDoc.type === "added") {
            if (!item.created && !item.timestamp) {
              // Skip items without a `created` timestamp
              return;
            }

            if (!item.deleted && !item.parentID) {
              // Add to comments if not already present
              if (!this.comments.some((msg) => msg.id === item.id)) {
                this.comments.push(item);
              }
            } else if (!item.deleted && item.parentID) {
              // Add to replies if not already present
              if (!this.replies.some((msg) => msg.id === item.id)) {
                this.replies.push(item);
              }
            }
          } else if (itemDoc.type === "removed") {
            // Handle "removed" comments
            this.performDeleteComment(item);
          } else if (itemDoc.type === "modified") {
            // Handle "modified" comments
            const updateComment = (msg) => {
              msg.message = item.message;
              msg.htmlMessage = item.htmlMessage;
              this.applyReactionFieldsFromDoc(msg, item);
              msg.flags = item.flags;
              this.buildHtmlComment(msg);
              if (item.edited) msg.edited = true;
            };

            if (item.deleted) {
              this.performDeleteComment(item);
            } else {
              let found = false;

              // Update or add to comments
              if (!item.parentID) {
                this.comments.forEach((msg) => {
                  if (msg.id === item.id) {
                    found = true;
                    updateComment(msg);
                  }
                });
              }

              // Update or add to replies
              else if (this.parentThreadMap[item.parentID]) {
                this.parentThreadMap[item.parentID].forEach((msg) => {
                  if (msg.id === item.id) {
                    found = true;
                    updateComment(msg);
                  }
                });
              }

              if (!found) {
                if (!item.deleted && !item.parentID) {
                  this.comments.push(item);
                } else if (!item.deleted && item.parentID) {
                  this.replies.push(item);
                }
              }
            }
          }
        });

        // Process replies
        this.replies.forEach((item) => {
          if (!this.processedCommentMap[item.id]) {
            this.processedCommentMap[item.id] = true;
            if (this.parentThreadMap[item.parentID]) {
              this.parentThreadMap[item.parentID].push(item);
            }
          }
        });

        // Sort comments
        let sortInt = sortOrder === "asc" ? -1 : 1;
        this.comments.sort((a, b) => {
          return b.created > a.created ? sortInt : -sortInt;
        });

        return this.comments;
      })
    );
  }

  performDeleteComment(item) {
    if (item.parentID) {
      let deleteIndex;
      this.parentThreadMap[item.parentID].forEach((msg, index) => {
        if (msg.id == item.id) deleteIndex = index;
      });
      this.parentThreadMap[item.parentID].splice(deleteIndex, 1);
    } else {
      let deleteIndex;
      this.comments.forEach((msg, index) => {
        if (msg.id == item.id) deleteIndex = index;
      });
      if (deleteIndex || deleteIndex === 0) this.comments.splice(deleteIndex, 1);
    }
  }

  deletePost(id, admin?, collection: string = environment.communityFirestoreCollection) {
    let args: any = {
      deleted: {
        timestamp: new Date(),
        deleted_by: this.userService.user.id,
      },
    };
    if (admin) args.deleted.admin = true;
    return this.fbService.doc(`${collection}/${id}`).update(args);
  }

  likeUnlikePost(post, isLiking?, collection: string = environment.communityFirestoreCollection) {
    const userId = this.userService.user.id;
    const current = getMyReactions(normalizeReactions(post), userId);
    // When isLiking is explicit, force add/remove Like; otherwise toggle.
    let nextTypes: ReactionType[];
    if (isLiking === true) {
      nextTypes = current.includes("like") ? current : [...current, "like"];
    } else if (isLiking === false) {
      nextTypes = current.filter((type) => type !== "like");
    } else {
      nextTypes = toggleReaction(current, "like");
    }
    return this.setReaction(post, nextTypes, collection);
  }

  /**
   * Persist the user's reaction set for a post.
   * `nextTypes` must be the final desired types (UI already toggled optimistically — do not toggle again).
   */
  setReaction(post, nextTypes: ReactionType[], collection: string = environment.communityFirestoreCollection) {
    const userId = this.userService.user.id;
    const types = Array.isArray(nextTypes) ? nextTypes : [];
    const current = normalizeReactions(post);
    const nextMap = { ...current };
    if (types.length) {
      nextMap[userId] = types;
    } else {
      delete nextMap[userId];
    }
    const likeCount = Object.keys(nextMap).length;
    // Firestore rules currently allow reaction writes when paired with likes[] updates
    // (creating `reactions` alone on a doc is permission-denied). Dual-write likes as a gate;
    // normalizeReactions still prefers the reactions map over legacy likes for display.
    const update: Record<string, any> = {
      likeCount,
      [`reactions.${userId}`]: types.length ? types : this.fbService.deleteField(),
      likes: types.length ? this.fbService.arrayUnion(userId) : this.fbService.arrayRemove(userId),
    };
    return this.fbService.doc(`${collection}/${post.key}`).update(update);
  }

  /**
   * Save/unsave ("pin" in code and data — the `pins` array) for the signed-in user.
   *
   * Applied optimistically and broadcast on `postPinsChanged$`, because nothing else will
   * tell the other views. The feed watcher skips `hasPendingWrites` snapshots, and the
   * list row and the detail pane can each hold their own copy of the post — so saving in
   * the detail used to leave the row's bookmark stale until a reload.
   */
  pinUnpinPost(post, collection: string = environment.communityFirestoreCollection) {
    const userId = this.userService.user.id;
    const previous: string[] = Array.isArray(post.pins) ? post.pins : [];
    const wasPinned = previous.includes(userId);
    this._setPins(post, wasPinned ? previous.filter((id) => id !== userId) : [...previous, userId]);
    return this.fbService
      .doc(`${collection}/${post.key}`)
      .update({
        pins: wasPinned ? this.fbService.arrayRemove(userId) : this.fbService.arrayUnion(userId),
      })
      .catch((error) => {
        this._setPins(post, previous);
        throw error;
      });
  }

  private _setPins(post, pins: string[]) {
    post.pins = pins;
    this.postPinsChanged$.next({ id: post.id, pins });
  }

  adminPinUnpinPost(post, collection: string = environment.communityFirestoreCollection) {
    const isBetaManager = this.userService.user.groups.some((group: any) => group.name === "beta_manager");
    if (!isBetaManager) {
      return Promise.reject(new Error("Only beta managers can pin/unpin posts."));
    }

    return this.fbService.doc(`${collection}/${post.key}`).update({
      pinned: post.pinned ? false : true,
    });
  }

  /**
   * A community's Featured rail (DENG-1294), newest featured first. Two equality filters, so
   * Firestore serves it from single-field indexes — no composite index to deploy — and the
   * order is applied here; the rail is capped small enough for that to be free.
   *
   * Firestore rather than Algolia: `featured` isn't a facet on the posts index, and the rail
   * has to reflect a moderator's change at once rather than after the sync lands.
   */
  getFeaturedPosts(communityStringId: string, collection: string = environment.communityFirestoreCollection): Promise<any[]> {
    if (!communityStringId) return Promise.resolve([]);
    return this.fbService
      .handleFirestorePromise(() => this.db.collection(collection).ref.where("community_string_id", "==", communityStringId).where("featured", "==", true).get())
      .then((snap: any) => {
        const posts = (snap?.docs || []).map((doc) => {
          const data: any = doc.data();
          data.key = doc.id;
          data.id = doc.id;
          return data;
        });
        return sortFeaturedPosts(posts).map((post) => {
          if (!post.htmlMessage) this.buildHtmlComment(post);
          return post;
        });
      })
      .catch((err) => {
        console.error("getFeaturedPosts failed", err);
        return [];
      });
  }

  /**
   * Feature or un-feature a post. Moderator-only: callers gate the control on
   * `CommunitiesService.isCommunityModerator`, and Firestore rules refuse the write from
   * anyone else. Broadcast on `featuredChanged$` once it lands.
   */
  setPostFeatured(post: any, featured: boolean, collection: string = environment.communityFirestoreCollection): Promise<void> {
    const userId = this.userService.user?.id;
    if (!userId || !post?.key) return Promise.reject(new Error("Sign in to feature posts."));
    const update = featuredUpdate(featured, userId, this.fbService.serverTimestamp());
    return this.fbService
      .doc(`${collection}/${post.key}`)
      .update(update)
      .then(() => {
        // The feeds' Algolia copy lags this write; this device's change wins meanwhile.
        rememberFeaturedChange(post.id || post.key, featured);
        post.featured = featured;
        post.featured_by = update["featured_by"];
        post.featured_at = featured ? new Date() : null;
        this.featuredChanged$.next({ communityStringId: post.community_string_id || FSP_COMMUNITY_STRING_ID, post, featured });
      });
  }

  /**
   * Open or close comments on a pinned beta announcement. Pinned posts are read-only by default;
   * this is the opt-in. Unlike adminPinUnpinPost, admins are allowed here alongside beta managers.
   */
  setAnnouncementCommentsAllowed(post, allow: boolean, collection: string = "beta_community_posts") {
    const groupNames = (this.userService.user?.groups || []).map((group: any) => group.name);
    if (!groupNames.includes("beta_manager") && !groupNames.includes("admin")) {
      return Promise.reject(new Error("Only beta managers and admins can change comment settings."));
    }

    return this.fbService.doc(`${collection}/${post.key}`).update({
      allow_comments: allow,
    });
  }

  /**
   * Fetch the existing replies for a set of parent posts into activeParentThreadMap.
   *
   * The feed loads replies as a side effect of loading its parents; announcements are fetched
   * separately by fetchBetaAnnouncements, so their history has to be pulled explicitly. New replies
   * created while the page is open already arrive via watchNewPosts.
   */
  loadRepliesForPosts(postIds: string[], collection: string = "beta_community_posts") {
    if (!postIds?.length) return Promise.resolve();

    return Promise.all(
      postIds.map((postId) =>
        this.fbService
          .collection(collection)
          .ref.where("parentID", "==", postId)
          .get()
          .then((replies) => {
            if (!this.activeParentThreadMap[postId]) this.activeParentThreadMap[postId] = [];
            replies.docs.forEach((replyDoc) => {
              const reply: any = replyDoc.data();
              if (reply.deleted) return;
              reply.key = replyDoc.id;
              reply.id = replyDoc.id;
              this.buildHtmlComment(reply);
              const alreadyAdded = this.activeParentThreadMap[postId].some((msg) => msg.id === reply.id);
              if (!alreadyAdded) this.activeParentThreadMap[postId].push(reply);
              this.displayedPostsMap[reply.key] = reply;
            });
            this.activeParentThreadMap[postId].sort((a, b) => (a.created > b.created ? 1 : -1));
          })
      )
    ).then(() => undefined);
  }

  markUnmarkSphereOnly(post, collection: string = environment.communityFirestoreCollection) {
    return this.fbService.doc(`${collection}/${post.key}`).update({
      sphere_only: post.sphere_only ? false : true,
    });
  }

  flagUnflagPost(post, reason?, collection: string = environment.communityFirestoreCollection) {
    if (post.flags && post.flags.includes(this.userService.user.id)) {
      return this.fbService.doc(`${collection}/${post.key}`).update({
        flags: this.fbService.arrayRemove(this.userService.user.id),
      });
    } else {
      return this.fbService.doc(`${collection}/${post.key}`).update({
        latest_flag_reason: reason,
        cleared: false,
        flags: this.fbService.arrayUnion(this.userService.user.id),
      });
    }
  }

  clearFlag(id, collection: string = environment.communityFirestoreCollection) {
    return this.fbService.doc(`${collection}/${id}`).update({ cleared: true });
  }

  updatePost(id, args, collection: string = environment.communityFirestoreCollection) {
    return super.updatePost(this.fbService.doc(`${collection}/${id}`), args);
  }

  getConnectedUserMessageThreads() {
    return super.getConnectedUserMessageThreads(this.fbService.collection("connection_threads").ref);
  }

  watchMissedMessageCounts() {
    if (this.missedMessageSubscription) this.missedMessageSubscription.unsubscribe();
    this.missedMessageSubscription = this.fbService
      .handleFirestoreObservable(() =>
        this.db
          .collection("connection_threads", (ref) => {
            return ref.where("users", "array-contains-any", [this.userService.user.id]);
          })
          .snapshotChanges()
          .pipe(
            throttle(
              () => {
                return interval(500);
              },
              { leading: true, trailing: true }
            )
          )
      )
      .subscribe((result: any) => {
        if (!this.userService.user) {
          if (this.missedMessageSubscription) this.missedMessageSubscription.unsubscribe();
        } else {
          this.unreadCounts.total = 0;
          this.unreadCounts.threads = {};
          result.forEach((item) => {
            let data: any = item.payload.doc.data();
            if (!data.inactive || !data.inactive.length) {
              let docID = item.payload.doc.id;
              if (data.viewing) {
                let obj = {};
                data.viewing.forEach((id) => {
                  obj[id] = true;
                });
                this.viewing[docID] = obj;
              }
              if (data.missedMessages) {
                if (!this.activeThread || this.activeThread.id != docID) {
                  this.unreadCounts.total += data.missedMessages[this.userService.user.id] ? data.missedMessages[this.userService.user.id] : 0;
                  this.unreadCounts.threads[docID] = data.missedMessages[this.userService.user.id];
                }
              }
            }
          });
        }
      });
  }

  getUnreadNotificationCount() {
    // Use native Firestore reference directly to avoid NG0203 error
    // when called from within .then() callbacks (outside injection context).
    // AngularFirestoreCollection uses inject(Injector) as a class property,
    // which fails when called outside Angular's injection context.
    //
    // NEWEST first: ascending `created` with a limit counted unseen among the OLDEST 100, so past
    // 100 notifications the badge counted the wrong ones. (Not `where("seen", "==", false)` —
    // paeapi writes notifications without a `seen` field and Firestore can't match a missing one.)
    const query = this.db.firestore.collection("user_profiles").doc(this.userService.user.id).collection("notifications").orderBy("created", "desc").limit(100);
    return super.getUnreadNotificationCount(query);
  }

  listenNotificationUpdates() {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }

    this.notificationSubscription = this.fbService
      .handleFirestoreObservable(() =>
        this.db
          .collection("user_profiles")
          .doc(this.userService.user.id)
          .collection("notifications")
          .snapshotChanges()
          .pipe(
            map((changes: any) =>
              changes.map((c) => {
                let rtn = { ...c.payload.doc.data(), key: c.payload.doc.id };
                return rtn;
              })
            )
          )
      )
      .subscribe((result: any) => {
        if (!this.userService.user) {
          this.notificationSubscription.unsubscribe();
        } else {
          this.notificationCount = result.length;
          this.unreadNotificationCount = 0;
          result.forEach((data) => {
            if (!data.seen) this.unreadNotificationCount++;
          });
        }
      });
  }

  getNotifications() {
    return super.getNotifications(
      this.fbService.collection(`user_profiles/${this.userService.user.id}/notifications`, (ref) => {
        return ref.limit(100).orderBy("created");
      }).ref
    );
  }

  deleteNotification(id) {
    return super.deleteNotification(this.fbService.doc(`user_profiles/${this.userService.user.id}/notifications/${id}`)).then(() => {
      return super
        .updateNotification(this.fbService.doc(`user_profiles/${this.userService.user.id}`), {
          notificationsTag: this.fbService.increment(1),
        })
        .then(() => {
          this.getUnreadNotificationCount();
        });
    });
  }

  batchDeleteNotifications(ids: Array<string>) {
    let batch = this.db.firestore.batch();
    ids.forEach((id) => {
      batch.delete(this.fbService.doc(`user_profiles/${this.userService.user.id}/notifications/${id}`).ref);
    });
    return batch.commit().then(() => {
      return this.fbService
        .doc(`user_profiles/${this.userService.user.id}`)
        .update({
          notificationsTag: this.fbService.increment(1),
        })
        .then(() => {
          this.getUnreadNotificationCount();
        });
    });
  }

  batchUpdateNotifications(ids: Array<string>, data: any) {
    let batch = this.db.firestore.batch();
    ids.forEach((id) => {
      batch.update(this.fbService.doc(`user_profiles/${this.userService.user.id}/notifications/${id}`).ref, data);
    });
    return batch.commit().then(() => {
      this.getUnreadNotificationCount();
    });
  }

  updateNotification(id, data) {
    super.updateNotification(this.fbService.doc(`user_profiles/${this.userService.user.id}/notifications/${id}`), data).then(() => {
      this.getUnreadNotificationCount();
    });
  }

  getTopic(string_id) {
    return this.fbService
      .handleFirestorePromise(() => this.db.collection("community_topics").doc(string_id).ref.get())
      .then((result) => {
        if (result.exists) {
          return result.data();
        } else {
          return false;
        }
      });
  }

  getTopics(all?, includeArchived = true) {
    let query: firebase.firestore.Query;

    if (all) {
      query = this.fbService.collection("community_topics").ref;
    } else if (includeArchived) {
      query = this.fbService.collection("community_topics").ref.where("start_date", "<", new Date());
    } else {
      query = this.fbService.collection("community_topics").ref.where("archived", "==", false).where("start_date", "<", new Date());
    }

    return this.fbService
      .handleFirestorePromise(() => query.get())
      .then((result) => {
        let rtn = [];
        if (!result.empty) {
          result.docs.forEach((topic) => {
            let data: any = topic.data();
            this.topicMap[data.string_id] = data;
            rtn.push(data);
          });
        }
        this.topics = rtn;
        return rtn;
      });
  }

  addTopic(data) {
    data.created_by = this.userService.user.id;
    data.created = new Date();
    data.archived = false;
    return this.fbService
      .handleFirestorePromise(() => this.db.collection("community_topics").doc(data.string_id).set(data))
      .then(() => {
        this.getTopics();
      });
  }

  updateTopic(string_id, data) {
    return this.fbService
      .doc(`community_topics/${string_id}`)
      .update(data)
      .then(() => {
        this.getTopics();
      });
  }

  toggleArchiveTopic(string_id, archived) {
    return this.fbService
      .doc(`community_topics/${string_id}`)
      .update({ archived: archived, date_archived: new Date(), archived_by: this.userService.user.id })
      .then(() => {
        this.getTopics();
      });
  }

  createConnectedUserMessageThread(user_id) {
    return super.createConnectedUserMessageThread(this.fbService.collection("connection_threads").ref, user_id);
  }

  removeConnection(connectionId, user_id) {
    return super.removeConnection(connectionId).pipe(
      map((result) => {
        this.fbService
          .collection("connection_threads")
          .ref.where("users", "in", [[this.userService.user.id, user_id]])
          .get()
          .then((threadResult) => {
            if (threadResult.docs && threadResult.docs.length) {
              threadResult.docs.forEach((doc) => {
                let data: any = doc.data();
                let connection = data.users.filter((id) => id == user_id)[0];
                if (connection) {
                  doc.ref.update({
                    inactive: this.fbService.arrayUnion(this.userService.user.id),
                  });
                }
              });
            }
          });
        return result;
      })
    );
  }

  getAnnouncements() {
    return this.fbService
      .handleFirestorePromise(() => this.db.collection("sphere_announcements").ref.get())
      .then((result) => {
        let announcements = [];
        result.docs.forEach((doc: any) => {
          let data = doc.data();
          announcements.push({
            ...data,
            id: doc.id,
          });
        });
        return announcements;
      });
  }

  deleteAnnouncement(id) {
    return this.fbService.doc(`sphere_announcements/${id}`).delete();
  }

  addAnnouncement(id, args) {
    return this.fbService.doc(`sphere_announcements/${id}`).set(args);
  }

  addBetaAnnouncement(betaStringId, announcementStringId, args) {
    return this.fbService.doc(`beta_communities/${betaStringId}/announcements/${announcementStringId}`).set(args);
  }

  getSplashes() {
    return this.fbService
      .handleFirestorePromise(() => this.db.collection("product_splashes").ref.get())
      .then((result) => {
        let splashes = [];
        result.docs.forEach((doc: any) => {
          let data = doc.data();
          splashes.push({
            ...data,
            id: doc.id,
          });
        });
        return splashes;
      });
  }

  deleteSplash(id) {
    return this.fbService.doc(`product_splashes/${id}`).delete();
  }

  addSplash(id, args) {
    return this.fbService.doc(`product_splashes/${id}`).set(args);
  }

  addNotification(user_id, notificationData) {
    notificationData.created = new Date();
    notificationData.seen = false;
    return this.fbService.collection(`user_profiles/${user_id}/notifications`).add(notificationData);
  }

  getStats(collection: string = environment.communityFirestoreCollection) {
    let promises = [];
    let stats = [];
    promises.push(
      this.fbService
        .collection(collection)
        .ref.where("parentID", "==", null)
        .get()
        .then((result) => {
          stats.push({
            title: "Original Posts",
            value: result.size,
          });
          let authors: any = {};
          let uniqueAuthorCount = 0;
          result.docs.forEach((doc) => {
            let data: any = doc.data();
            if (!authors[data.author.id]) {
              authors[data.author.id] = 0;
              uniqueAuthorCount++;
            }
            authors[data.author.id]++;
          });
          stats.push({
            title: "Unique Authors",
            value: uniqueAuthorCount,
            data: {
              description: "Post count per user ID.",
              values: authors,
            },
          });
        }),
      this.fbService
        .collection(collection)
        .ref.where("parentID", "!=", null)
        .get()
        .then((result) => {
          stats.push({
            title: "Replies",
            value: result.size,
          });
        }),
      this.fbService
        .collection("user_profiles")
        .ref.where("hasSetupCommunity", "==", true)
        .get()
        .then((result) => {
          stats.push({
            title: "Total Community Members",
            value: result.size,
          });
        }),
      this.fbService
        .collection("user_profiles")
        .ref.where("hasSetupCommunity", "==", true)
        .where("public", "==", true)
        .get()
        .then((result) => {
          stats.push({
            title: "Public Members",
            value: result.size,
          });
        }),
      this.fbService
        .collection("video_comments")
        .ref.get()
        .then((result) => {
          let totalComments = 0;
          let subPromises = [];
          let videoComments: Array<any> = [];
          result.docs.forEach((doc) => {
            subPromises.push(
              doc.ref
                .collection("comments")
                .get()
                .then((result) => {
                  if (result.docs.length) {
                    videoComments.push({
                      comments: result.docs.length,
                      title: (<any>doc.data()).title,
                      id: doc.id,
                    });
                    totalComments += result.docs.length;
                  }
                })
            );
          });
          return Promise.all(subPromises).then(() => {
            videoComments.sort((a, b) => {
              return a.comments > b.comments ? -1 : 1;
            });
            stats.push({
              title: "Total Video Comments",
              value: totalComments,
              data: {
                description: "Comment counts by video",
                values: videoComments,
              },
            });
          });
        })
    );
    return new Promise((resolve, reject) => {
      Promise.all(promises).then((result) => {
        resolve(stats);
      });
    });
  }

  parseEmbedUrls(text: string, compareArray?: MessageEmbed[]): MessageEmbed[] {
    const embeds = super.parseEmbedUrls(text, compareArray);
    if (embeds) {
      return embeds.map((embed) => ({
        ...embed,
        embedSrc: this._sanitizer.bypassSecurityTrustResourceUrl(embed.embedSrc as string),
      }));
    } else {
      return [];
    }
  }

  buildHtmlComment(item, includeMentions = true, makeEditable = false) {
    item = super.buildHtmlComment(item, includeMentions, makeEditable);
    if (item?.htmlMessage) {
      const withVideoChips = this.replaceViewingVideoLinksWithTitles(item.htmlMessage);
      const nextHtml = this.sanitizeMessageHtml(withVideoChips);
      if (nextHtml !== item.htmlMessage) {
        item.htmlMessage = nextHtml;
        item.htmlViewContent =
          '<span style="font-size: ' +
          (item.parentID ? "14" : "18") +
          "px; font-family: sans-serif; background-color: transparent; color: " +
          (this.helperService.mobileTheme == "dark" ? "white" : "#333") +
          ';">' +
          item.htmlMessage +
          "</span>";
      }
    }
    return item;
  }

  /**
   * Last gate before the body reaches an `[innerHTML]` binding.
   *
   * Message bodies are user-written and, for older posts, stored as raw editor
   * HTML, so they cannot simply be escaped without surfacing markup as text.
   * Community and workspace pass the result through `bypassSecurityTrustHtml`,
   * and `chat.component.html` bound it with no sanitisation at all, so nothing
   * was filtering these strings. DOMPurify was already a dependency and unused.
   *
   * The allowlist covers the inline chips this app emits (mentions, articles,
   * shared files, learn content) plus the anchors `urlify` creates.
   */
  sanitizeMessageHtml(html: string): string {
    if (!html) return html;

    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ["a", "span", "div", "p", "br", "b", "strong", "i", "em", "u", "s", "blockquote", "code", "pre", "ul", "ol", "li"],
      ALLOWED_ATTR: [
        "href",
        "target",
        "rel",
        "class",
        "role",
        "tabindex",
        "aria-label",
        "contenteditable",
        // Quill 2 renders BOTH list types as <ol> and tells them apart with
        // `data-list="bullet"` / `"ordered"` on each <li>. Dropping it turns
        // every bullet list into a numbered one (PRS-2395).
        "data-list",
        "data-mentionid",
        "data-articleid",
        "data-sectionid",
        "data-categoryid",
        "data-videoid",
        "data-lessonid",
        "data-courseid",
        "data-shareid",
        "data-hash",
        "data-fileid",
      ],
      // Blocks javascript: and data: hrefs while leaving normal links alone.
      ALLOWED_URI_REGEXP: /^(?:https?|mailto|tel):/i,
    });
  }

  /**
   * Convert raw MyFender viewingVideo URLs (and urlify anchors) into titled inline-video
   * chips so comment + reply rendering stays consistent and clicks stay in-app.
   */
  private replaceViewingVideoLinksWithTitles(html: string): string {
    if (!html || html.indexOf("viewingVideo=") === -1) return html;

    const toChip = (rawId: string) => {
      const id = decodeURIComponent(rawId || "").split(/[&#?\s]/)[0];
      const video = this.contentService?.videosById?.[id];
      if (!video) return null;
      const title = String(video.title || id)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
      return `<span class="inline-video" data-videoid="${video.id}" role="button" tabindex="0" aria-label="Video: ${title}">${title}</span>`;
    };

    // Prefer replacing already-urlified anchors first.
    html = html.replace(/<a\b[^>]*href=["']([^"']*viewingVideo=([^"'&#\s]+))["'][^>]*>[\s\S]*?<\/a>/gi, (match, _url, videoId) => {
      return toChip(videoId) || match;
    });

    // Then convert any remaining bare viewingVideo URLs.
    html = html.replace(/\bhttps?:\/\/[^\s<]*viewingVideo=([^&\s<"']+)/gi, (match, videoId) => {
      return toChip(videoId) || match;
    });

    return html;
  }
}
